// Turn-based game engine (Warring Isles). Deterministic; no Math.random/Date.now
// in the sim itself (procedural gen is seeded). Phase A: world setup + a round
// counter. Economy / units / combat / tech / diplomacy / AI land in later phases.

import { mulberry32 } from '../engine/rng';
import { generateWorld } from './world';
import { RACES } from './factions';
import { hexDistance, hexKey, hexNeighbors } from './hex';
import { BUILDING, NEUTRAL, RESOURCES, impassable, type DiploOffer, type Difficulty, type Faction, type FactionId, type FactionStrategy, type GameMode, type GameState, type GameStats, type Handicap, type BuildingType, type LogEvent, type MapKind, type Region, type RegionId, type ResAmt, type Unit, type UnitType, type Voyage } from './types';

const START_STOCK = 3; // everyone starts with 3 of each resource
const BASE_INCOME = 1; // per region, per each of its island's 2 resources, per turn
const MARKET_BONUS = 1; // extra income of each island resource from a Market
const MARKET_TURNS = 2;
const MARKET_AMOUNT = 2; // a Market costs 2 of each of its island's resources
const HARBOR_TURNS = 2;
const HARBOR_AMOUNT = 2; // a Harbor costs 2 of each of its island's resources

// --- combat (Phase C) ---
const UNIT_AMOUNT = 2; // cost to train a unit (of each of the race's two signature resources)
const ADVANCED_AMOUNT = 2; // extra cost (of the race's advanced resource) for tech-unlocked Archer/Rider
export const BASE_UNIT_CAP = 3; // army supply: everyone may field this many units at first; each Harbor adds +1
// Build time by unit type (Warrior, Archer, Rider, Spearman). Archer & Rider each out-match TWO other
// types (see BEATS), so they're the premium 3-turn units; Warrior & Spearman out-match only one, so they
// are quick, affordable 2-turn troops. Cost and durability (UNIT_HP) balance the asymmetric matchup wheel.
export const UNIT_TURNS: readonly number[] = [2, 3, 3, 2];
// Per-type max HP: the two single-counter units (Warrior, Spearman) are the tankiest (5) durable mass
// infantry; the two double-counter units pay for their reach: the Archer is a fragile 3-HP glass cannon
// (its range is its edge), the Rider a 4-HP shock cavalry. This offsets their matchup advantage.
export const UNIT_HP: readonly number[] = [5, 3, 4, 5]; // Warrior, Archer, Rider, Spearman
export const unitMaxHp = (type: UnitType): number => UNIT_HP[type] ?? 4;
// HP / damage combat (health pips, ranged archers, retaliation)
export const MAX_HP = 5; // the most HP any unit can have, also the single-hit damage cap (no one-shots past it)
const BASE_DMG = 2; // damage a hit deals before modifiers
const RPS_DMG = 1; // extra damage on a favourable rock-paper-scissors matchup
// An arrow lands lighter than a blade: a ranged strike gives up one point of damage. Without this an
// Archer whose realm has researched mil1+mil2 deals a flat 5, which is exactly a healthy Warrior's or
// Spearman's whole health bar, so it kills any of the three other types outright, every round, from two
// tiles away, taking nothing back (scripts/dmgtable.ts). "Strong against" has to mean favourable, not
// free deletion. With the point removed every one of those becomes a two-arrow kill.
const RANGED_DMG_PENALTY = 1;
const ARCHER_RANGE = 2; // an Archer can strike a target up to this many land tiles away
const RIDER: UnitType = 2; // cavalry: gets TWO move points a turn (see unitMoves), so it can move then move again, or charge (move then attack)
/** Move points a unit gets each turn: Riders get 2 (a land move spends 1; an attack or a sea crossing spends them all), everyone else 1. */
export const unitMoves = (type: UnitType): number => (type === RIDER ? 2 : 1);
const CAPITAL_DMG_REDUCE = 1; // a defender on its capital takes 1 less
const BARRACKS_DMG_REDUCE = 1; // defenders standing on a Barracks take this much less (a fortified camp)
const HARBOR_GOLD = 1; // a Harbor's trade yields this much Gold per turn (it grants no army supply)
const CAPITAL_INCOME = 2; // the seat of power is a richer tile: +this of each of its island's goods per turn
const PLUNDER_SHARE = 0.35; // capturing an enemy capital raids this share of that faction's whole stockpile
const SEA_DMG_PENALTY = 1; // a cross-sea attacker deals 1 less (removed by Seafaring)
const RETALIATION_PENALTY = 1; // a defender's counter-attack is weaker than a full strike, so attacking pays off
const REGEN_HP = 1; // HP a unit recovers each turn it doesn't act, on friendly land
export const NO_WAR_ROUNDS = 2; // grace period: wars can be declared from round 3 (round <= this is blocked).
// Kept short on purpose: a boxed-in unit can't attack its way out during the grace, so a long grace strands
// early sea-landings; short grace lets conflict (and unsticking) start almost immediately.
// AI difficulty. Normal = today's tuned constants (human wins ~38%); Hard makes the AIs press harder and trims the
// human's two war handicaps + its besieged last-stand shield. Read once per call as `DIFF[g.difficulty]` in
// runAiTurn's war-declaration block and in attackDamage. `warGrace`: the AIs leave the HUMAN alone until this round
// (a casual on-ramp; the player's OWN wars still open at round 3). `warMargin`: extra strength edge an AI needs to
// declare on the human vs on a rival AI. `biasShift`/`warGateAlways`: how eagerly every personality goes to war.
// `lastStand*`: region thresholds for the besieged-human damage shield. `closer*`: the endgame "close it out" rule.
const DIFF = {
  // closerLead 1.0 on both: in a 1v1 SOMEONE must attack or the game drags to the round cap, so the leader (or the
  // tied party) commits rather than staring at a stalemate; the weaker side still just defends (mine < rivalMax).
  normal: { warGrace: 5, warMargin: 3, biasShift: 0, warGateAlways: false, lastStandSoft: 4, lastStandHard: 2, closerMaxFactions: 1, closerLead: 1.0 },
  hard: { warGrace: 3, warMargin: 1, biasShift: 1, warGateAlways: true, lastStandSoft: 2, lastStandHard: 1, closerMaxFactions: 2, closerLead: 1.0 },
} as const;
const DIPLO_COOLDOWN = 3; // after declaring war you must fight ≥3 turns before suing for peace, and vice-versa
const POST_ALLY_WAR_GRACE = 1; // breaking an alliance imposes a one-turn cool-down before either side may declare war

// "Strong against" matrix (asymmetric by design, see UNIT_HP/UNIT_TURNS for the balancing). BEATS[a]
// lists every type `a` gets the +RPS_DMG bonus against: Riders run down Warriors & Archers, Archers pepper
// Warriors & Spearmen, Warriors break Spearmen, Spearmen gut Riders. COUNTER[e] is one canonical unit that
// beats type e, used by the AI to counter-pick the most common threatening enemy type.
const BEATS: readonly (readonly UnitType[])[] = [
  [3], // Warrior  → Spearman
  [0, 3], // Archer   → Warrior, Spearman
  [0, 1], // Rider    → Warrior, Archer
  [2], // Spearman → Rider
];
// Canonical counter per enemy type: a cycle (Warrior←Archer←Rider←Spearman←Warrior) so the AI spreads its
// counter-production evenly across all four units instead of leaning on one; every entry does beat its target.
const COUNTER: readonly UnitType[] = [1, 2, 3, 0]; // beat Warrior→Archer, Archer→Rider, Rider→Spearman, Spearman→Warrior
const beats = (a: UnitType, b: UnitType): boolean => BEATS[a]!.includes(b);

// --- tech tree (Phase D): 3 branches × 3 tiers, branching by `req` ---
export type TechBranch = 'mil' | 'eco' | 'def';
export interface Tech {
  id: string;
  name: string;
  branch: TechBranch;
  tier: 1 | 2 | 3;
  req: string | null; // prerequisite tech id
  gold: number; // Gold cost
  turns: number;
  desc: string;
  unlocks?: UnitType; // researching this also lets you train this unit type (Archer / Rider)
}
export const TECHS: Tech[] = [
  { id: 'mil1', name: 'Archery', branch: 'mil', tier: 1, req: null, gold: 4, turns: 1, desc: 'unlock 🏹 Archer + 1 attack damage', unlocks: 1 },
  { id: 'mil2', name: 'Cavalry', branch: 'mil', tier: 2, req: 'mil1', gold: 12, turns: 2, desc: 'unlock 🐎 Rider + 1 more attack damage', unlocks: 2 },
  { id: 'mil3', name: 'Seafaring', branch: 'mil', tier: 3, req: 'mil2', gold: 18, turns: 3, desc: 'no penalty on sea assaults' },
  // the other tier-3 war science, available on every map: siege engines break fortifications, so walls,
  // gatehouses and the extra toughness of a capital all stop mattering to your attacks.
  { id: 'sie3', name: 'Siegecraft', branch: 'mil', tier: 3, req: 'mil2', gold: 18, turns: 3, desc: 'attacks ignore Walls, gates and capital defence' },
  { id: 'eco1', name: 'Masonry', branch: 'eco', tier: 1, req: null, gold: 4, turns: 1, desc: 'buildings & units cost 1 less each' },
  { id: 'eco2', name: 'Trade Routes', branch: 'eco', tier: 2, req: 'eco1', gold: 12, turns: 2, desc: '+1 income per region' },
  { id: 'eco3', name: 'Grand Markets', branch: 'eco', tier: 3, req: 'eco2', gold: 18, turns: 3, desc: 'Markets give +2 instead of +1' },
  { id: 'def1', name: 'Shields', branch: 'def', tier: 1, req: null, gold: 4, turns: 1, desc: 'units take 1 less damage' },
  { id: 'def2', name: 'Ramparts', branch: 'def', tier: 2, req: 'def1', gold: 12, turns: 2, desc: 'Walls block 2 damage' },
  { id: 'def3', name: 'Watchtowers', branch: 'def', tier: 3, req: 'def2', gold: 18, turns: 3, desc: 'attackers of your land deal 1 less' },
];
const GOLD = 0; // resource index

const has = (f: Faction, id: string): boolean => f.techs.includes(id);
const atkDmgBonus = (f: Faction): number => (has(f, 'mil1') ? 1 : 0) + (has(f, 'mil2') ? 1 : 0); // +damage dealt
const defDmgReduce = (f: Faction): number => (has(f, 'def1') ? 1 : 0); // takes less damage
const wallDmgReduce = (f: Faction): number => (has(f, 'def2') ? 2 : 1); // a Wall softens hits (Ramparts more)
const watchtowerReduce = (f: Faction): number => (has(f, 'def3') ? 1 : 0); // invaders of your land hit weaker
const costReduce = (f: Faction): number => (has(f, 'eco1') ? 1 : 0);
const regionIncomeBonus = (f: Faction): number => (has(f, 'eco2') ? 1 : 0);
const marketBonus = (f: Faction): number => (has(f, 'eco3') ? 2 : MARKET_BONUS);

/** One seat at the table: who controls this faction and, for a human, which race they picked.
 *  Slots without a race are dealt a distinct remaining race deterministically from the seed. */
export interface SlotSpec {
  race?: string; // race key; omitted → dealt from the remaining races (seeded shuffle)
  human?: boolean; // a human sits here (solo: exactly one; multiplayer: any number)
  aiDifficulty?: Difficulty; // AI seats only: per-slot difficulty (multiplayer host choice)
}

/** Full deterministic match setup: identical config on every client → identical starting GameState. */
export interface MatchConfig {
  cliffs?: boolean; // curated free-play boards may sprinkle island cliffs; scripted worlds stay flat
  heart?: boolean; // curated isles boards may add the neutral HEART ISLE prize at the wheel's centre
  capitals?: boolean; // free-play CROWN RULE: extra capitals held raise the army cap, holding none lowers it
  seed: number;
  factionCount: number;
  islandSize?: number;
  dominationPct?: number;
  difficulty?: Difficulty; // default AI difficulty (per-slot aiDifficulty overrides)
  mode?: GameMode; // 'solo' (default) keeps the single-player human perks; 'mp' is fully symmetric
  mapKind?: MapKind; // 'isles' (default) archipelago, or 'land': one connected mainland (no sea)
  slots?: SlotSpec[]; // per-faction seats, index = faction id; missing entries are default AI seats
}

/** Build a fresh solo game (the classic single-player entry): the human is faction 0 with their pick. */
export function createGame(seed: number, playerRaceKey: string, factionCount: number, islandSize?: number, dominationPct: number = DOMINATION_PCT, difficulty: Difficulty = 'normal', mapKind: MapKind = 'isles', cliffs = false, heart = false, capitals = false): GameState {
  return createMatch({ seed, factionCount, islandSize, dominationPct, difficulty, mode: 'solo', mapKind, cliffs, heart, capitals, slots: [{ race: playerRaceKey, human: true }] });
}

/** Build a fresh game from a full match config: generate the world, seat the slots, seed capitals. */
export function createMatch(cfg: MatchConfig): GameState {
  const { seed, factionCount } = cfg;
  const dominationPct = cfg.dominationPct ?? DOMINATION_PCT;
  const difficulty = cfg.difficulty ?? 'normal';
  const mode: GameMode = cfg.mode ?? 'solo';
  const mapKind: MapKind = cfg.mapKind ?? 'isles';
  const slots: SlotSpec[] = Array.from({ length: factionCount }, (_, i) => cfg.slots?.[i] ?? {});
  const { regions, islands } = generateWorld(seed, factionCount, cfg.islandSize, mapKind, cfg.cliffs ?? false, cfg.heart ?? false);
  const rng = mulberry32(seed ^ 0x5bd1e995);

  // race assignment: explicit picks are honoured; the rest are dealt distinct remaining races (seeded
  // shuffle). With the solo wrapper (slot 0 = the player's pick) this reproduces the classic behaviour.
  const picked = new Set(slots.map((s) => s.race).filter((r): r is string => !!r));
  const others = RACES.map((r) => r.key).filter((k) => !picked.has(k));
  for (let i = others.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = others[i]!;
    others[i] = others[j]!;
    others[j] = tmp;
  }

  const STRATEGIES: FactionStrategy[] = ['aggressive', 'defensive', 'opportunist', 'diplomat'];
  const factions: Faction[] = [];
  let dealt = 0;
  for (let i = 0; i < factionCount; i++) {
    const slot = slots[i]!;
    const key = slot.race ?? others[dealt++]!;
    const race = RACES.find((r) => r.key === key) ?? RACES[0]!;
    factions.push({
      id: i,
      race: key,
      name: race.name,
      color: race.color,
      isPlayer: !!slot.human,
      eliminated: false,
      strategy: STRATEGIES[Math.floor(rng() * STRATEGIES.length)]!, // dealt a random personality (seeded)
      ...(slot.aiDifficulty ? { aiDifficulty: slot.aiDifficulty } : {}),
      stock: new Array<number>(RESOURCES).fill(START_STOCK),
      techs: [],
      research: null,
    });
  }

  // Units are trained from race-specific resources (see unitCost). Make each home island PRODUCE its home
  // race's signature pair, so a faction can always fund its own army, and its research, from home income.
  // (island id === home faction id.) This overrides the generator's random resource pick for home islands.
  for (let i = 0; i < factionCount; i++) {
    const res = RACES.find((r) => r.key === factions[i]!.race)?.unitRes;
    if (res && islands[i]) islands[i]!.resources = res;
  }

  // each island's capital is owned by that island's home faction (island id === faction id),
  // and starts with a Warrior garrison so combat is possible from turn one
  // Everyone starts on equal footing: a single owned tile (the capital) with a Warrior garrison. Land is
  // won by marching that soldier onto neutral tiles and training more units on the tiles you free up.
  for (const reg of regions) {
    if (!reg.isCapital) continue;
    reg.owner = reg.island;
    reg.unit = { type: 0, owner: reg.island, movesLeft: unitMoves(0), hp: unitMaxHp(0) };
  }

  const stats: GameStats = {
    captured: new Array<number>(factionCount).fill(0),
    kills: Array.from({ length: factionCount }, () => [0, 0, 0, 0]),
    built: Array.from({ length: factionCount }, () => [0, 0, 0, 0]),
  };
  // fixed play order, shuffled at the start so it is NOT always the player who opens. The order holds for the
  // whole game: the factions listed before the player take their turn before the player each round (a short
  // opening replay on the first turn, folded into the AI phase after that), those after close the round out.
  const turnOrder = Array.from({ length: factionCount }, (_, i) => i);
  for (let i = turnOrder.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = turnOrder[i]!;
    turnOrder[i] = turnOrder[j]!;
    turnOrder[j] = tmp;
  }
  // playerFaction = the first human seat (solo: faction 0). turnOf = the human who acts first in play
  // order (the opening run advances the AI seats before them). All-AI sims fall back to faction 0.
  const playerFaction = factions.find((f) => f.isPlayer)?.id ?? 0;
  const turnOf = turnOrder.find((id) => factions[id]!.isPlayer) ?? playerFaction;
  return { round: 1, regions, islands, factions, playerFaction, mapKind, voyages: [], mode, turnOf, turnOrder, dominationPct, capitalRule: cfg.capitals ?? false, roundLimit: roundLimitFor(mapKind), difficulty, stats, selectedRegion: null, phase: 'playing', result: null, winner: null, alliances: [], wars: [], warStamp: {}, peaceStamp: {}, allyStamp: {}, offers: [], messages: [], nextOfferId: 1, log: [], replayBefore: [], replay: [] };
}

// --- economy helpers ---
const affordable = (stock: number[], cost: number[]): boolean => cost.every((c, i) => (stock[i] ?? 0) >= c);
const pay = (stock: number[], cost: number[]): void => cost.forEach((c, i) => (stock[i]! -= c));
const refund = (stock: number[], cost: number[]): void => cost.forEach((c, i) => (stock[i]! += c));
const reduceCost = (cost: number[], f: Faction): number[] => cost.map((c) => (c > 0 ? Math.max(1, c - costReduce(f)) : 0)); // Masonry (eco1)
// Storage cap: per-turn income can't pile a resource past this, so wealth becomes armies/tech/buildings instead of a
// meaningless growing heap (playtesting saw 1000+ idle wood). A flat, legible ceiling, comfortably above any single
// purchase (a tier-3 tech costs 12 Gold, an advanced unit ~6) so you can still bank for what you want, but tight
// enough that surplus must be SPENT, not hoarded. Only limits GROWTH: a stock already above it is never drained.
export const STOCK_CAP = 40;

/** A cost vector with `amount` set on each of the region's island resources. */
/** EXPANSION TAX: each copy of a building a realm already owns makes the next one +1/+1 dearer
 *  (capped at +4). The first couple stay cheap, a 12-region build-everything spree becomes a real
 *  budget: the other half of the anti-flood pass (see unitCost'''s recruitment fatigue + ecotest). */
function ownedCount(g: GameState, fid: FactionId, building: BuildingType): number {
  let n = 0;
  for (const r of g.regions) {
    if (r.owner !== fid) continue;
    if (r.buildings.includes(building)) n++;
    if (r.production?.kind === 'building' && r.production.building === building) n++;
  }
  return n;
}
const expansionTax = (cost: number[], g: GameState, fid: FactionId, building: BuildingType): number[] => {
  const tax = Math.min(4, ownedCount(g, fid, building));
  return cost.map((c) => (c > 0 ? c + tax : 0));
};

function islandCost(g: GameState, region: Region, amount: number): number[] {
  const cost = new Array<number>(RESOURCES).fill(0);
  for (const r of g.islands[region.island]!.resources) cost[r] = amount;
  return cost;
}

/** Per-turn resource income for a faction (sum of its regions; Markets + tech boost output). */
export function factionIncome(g: GameState, fid: FactionId): number[] {
  const inc = new Array<number>(RESOURCES).fill(0);
  const f = g.factions[fid]!;
  const perRegion = BASE_INCOME + regionIncomeBonus(f);
  let regions = 0;
  for (const reg of g.regions) {
    if (reg.owner !== fid) continue;
    regions += 1;
    const marketInc = reg.buildings.includes(BUILDING.Market) ? marketBonus(f) : 0;
    const capInc = reg.isCapital ? CAPITAL_INCOME : 0; // the seat of power yields extra of its island's goods
    for (const r of g.islands[reg.island]!.resources) inc[r]! += perRegion + marketInc + capInc;
    // a port is a TRADE house as well as a gangway: shipping pays in coin, not in soldiers
    if (reg.buildings.includes(BUILDING.Harbor)) inc[GOLD] = (inc[GOLD] ?? 0) + HARBOR_GOLD;
  }
  // Gold stipend: research is paid in Gold, but each race's units are built from its own signature pair,
  // and for the three Gold-using races that pair TAXES the same Gold that funds research. To keep research
  // open and fair to every race, hand every faction a flat +BASE_INCOME Gold per region. For Gold-less
  // races (Orcs, Undead) this is their only Gold, funding tech they otherwise couldn't touch; for Gold
  // races it offsets the Gold their troops consume, so no one is priced out of the tech tree by its race.
  inc[GOLD] = (inc[GOLD] ?? 0) + regions * BASE_INCOME;
  // Anti-soft-lock: units cost the race's FIXED signature resources, but income only ever yields the goods
  // of the islands a faction currently holds, and captured islands keep their own goods. A faction that
  // lost every island producing one of its signature resources would otherwise never train again. Floor
  // each signature resource at 1/turn, a no-op while it owns ANY home-resource tile (income is already
  // ≥1 there), and a slow, snowball-proof trickle that keeps a dispossessed faction able to rebuild.
  if (regions > 0) for (const res of factionUnitRes(g, fid)) inc[res] = Math.max(inc[res] ?? 0, 1);
  return inc;
}


// Neutral land is no longer bought: you take it by marching a unit onto it (see moveOrAttack /
// unitTargets, where an empty neutral tile is a valid "move" that transfers ownership).

// --- build a Market (turns + progress + cancellable refund) ---
export const marketCost = (g: GameState, region: Region, fid: FactionId): number[] => expansionTax(reduceCost(islandCost(g, region, MARKET_AMOUNT), g.factions[fid]!), g, fid, BUILDING.Market);

export function canBuildMarket(g: GameState, regionId: RegionId, fid: FactionId): boolean {
  const reg = g.regions[regionId];
  if (!reg || reg.owner !== fid || reg.production || reg.buildings.includes(BUILDING.Market)) return false;
  return affordable(g.factions[fid]!.stock, marketCost(g, reg, fid));
}

export function startMarket(g: GameState, regionId: RegionId, fid: FactionId): boolean {
  if (!canBuildMarket(g, regionId, fid)) return false;
  const reg = g.regions[regionId]!;
  const cost = marketCost(g, reg, fid);
  pay(g.factions[fid]!.stock, cost);
  const mt = prodTurns(g, fid, MARKET_TURNS);
  reg.production = { kind: 'building', building: BUILDING.Market, turnsLeft: mt, turnsTotal: mt, cost };
  return true;
}

// --- build a Harbor: a unit can only launch a sea assault from an island where its faction owns one ---
export const harborCost = (g: GameState, region: Region, fid: FactionId): number[] => expansionTax(reduceCost(islandCost(g, region, HARBOR_AMOUNT), g.factions[fid]!), g, fid, BUILDING.Harbor);

/** Does `fid` own a completed Harbor anywhere on `island`? (Gates every sea assault launched from it.) */
export function islandHasHarbor(g: GameState, fid: FactionId, island: number): boolean {
  return g.regions.some((r) => r.owner === fid && r.island === island && r.buildings.includes(BUILDING.Harbor));
}

// Coastline lookup: the set of hexes that ARE land on this map (mountains and walls included: rock is
// land, it just cannot be walked). Memoized per regions array, which never changes shape mid-match.
const landHexes = new WeakMap<object, Set<string>>();
function landKeySet(g: GameState): Set<string> {
  let set = landHexes.get(g.regions);
  if (!set) {
    set = new Set(g.regions.map((r) => hexKey(r.q, r.r)));
    landHexes.set(g.regions, set);
  }
  return set;
}

/** Coastal = the tile actually touches OPEN WATER: at least one of its six hex neighbours holds no
 *  land at all. (The old count-the-neighbours heuristic called any tile beside a mountain or a castle
 *  wall "coastal", which let players raise Harbors deep inland.) Harbors are coastal-only. */
export function isCoastal(g: GameState, region: Region): boolean {
  const land = landKeySet(g);
  return hexNeighbors(region.q, region.r).some((n) => !land.has(hexKey(n.q, n.r)));
}

// --- water bodies -------------------------------------------------------------------------------
// A BOAT NEVER CROSSES LAND. On a mainland map the open sea and each enclosed lake are separate
// waters: a lake port serves only that lake's shores, a sea port only the sea's, and two lakes never
// connect. (Distance alone used to gate mainland crossings, which let a lake boat "sail" overland to
// the outer coast.) The map below labels every water hex ringing the continent with a component id;
// id 0 is always the OPEN SEA. Memoized per regions array, which never changes shape mid-match.
const waterBodies = new WeakMap<object, Map<string, number>>();
function waterBodyMap(g: GameState): Map<string, number> {
  let map = waterBodies.get(g.regions);
  if (map) return map;
  map = new Map();
  const land = landKeySet(g);
  let minQ = Infinity, maxQ = -Infinity, minR = Infinity, maxR = -Infinity;
  for (const r of g.regions) {
    minQ = Math.min(minQ, r.q);
    maxQ = Math.max(maxQ, r.q);
    minR = Math.min(minR, r.r);
    maxR = Math.max(maxR, r.r);
  }
  minQ -= 1; maxQ += 1; minR -= 1; maxR += 1; // one water ring beyond the land suffices: everything farther is open sea
  let nextLake = 0;
  for (let q = minQ; q <= maxQ; q++) {
    for (let r = minR; r <= maxR; r++) {
      const key = hexKey(q, r);
      if (land.has(key) || map.has(key)) continue;
      // flood this pocket of water. If it reaches the bounding box edge it joins the infinite water
      // outside, which wraps around the continent unbroken: that is the open sea, id 0. A pocket the
      // land seals off completely is a LAKE and gets its own id.
      const cells: string[] = [key];
      const seen = new Set<string>([key]);
      const stack: { q: number; r: number }[] = [{ q, r }];
      let touchesEdge = q === minQ || q === maxQ || r === minR || r === maxR;
      while (stack.length) {
        const c = stack.pop()!;
        for (const n of hexNeighbors(c.q, c.r)) {
          if (n.q < minQ || n.q > maxQ || n.r < minR || n.r > maxR) continue;
          const nk = hexKey(n.q, n.r);
          if (land.has(nk) || seen.has(nk)) continue;
          seen.add(nk);
          cells.push(nk);
          if (n.q === minQ || n.q === maxQ || n.r === minR || n.r === maxR) touchesEdge = true;
          stack.push(n);
        }
      }
      const id = touchesEdge ? 0 : ++nextLake;
      for (const k of cells) map!.set(k, id);
    }
  }
  waterBodies.set(g.regions, map);
  return map;
}

/** The water bodies lapping a tile's shore (empty for a landlocked tile). */
function shoreWaters(g: GameState, region: Region): Set<number> {
  const map = waterBodyMap(g);
  const out = new Set<number>();
  for (const n of hexNeighbors(region.q, region.r)) {
    const id = map.get(hexKey(n.q, n.r));
    if (id !== undefined) out.add(id);
  }
  return out;
}

/** The water body under a hex (undefined = that hex is land). For the renderer's boat anchoring. */
export const waterBodyAt = (g: GameState, q: number, r: number): number | undefined => waterBodyMap(g).get(hexKey(q, r));

/** The water bodies lapping a tile's shore, exported for the renderer (see shoreWaters above). */
export const shoreWatersOf = (g: GameState, region: Region): Set<number> => shoreWaters(g, region);

/** True when one CONTINUOUS body of water touches both shores: the only geometry a boat may cross. */
export function sharesWater(g: GameState, a: Region, b: Region): boolean {
  const wa = shoreWaters(g, a);
  for (const id of shoreWaters(g, b)) if (wa.has(id)) return true;
  return false;
}

/** A Harbor already stands on `island` OR one is being built there, used to block redundant second harbors. */
function islandHarborPlanned(g: GameState, fid: FactionId, island: number): boolean {
  return g.regions.some(
    (r) =>
      r.owner === fid &&
      r.island === island &&
      (r.buildings.includes(BUILDING.Harbor) || (r.production?.kind === 'building' && r.production.building === BUILDING.Harbor)),
  );
}

export function canBuildHarbor(g: GameState, regionId: RegionId, fid: FactionId): boolean {
  const reg = g.regions[regionId];
  if (!reg || reg.owner !== fid || reg.production || reg.buildings.includes(BUILDING.Harbor)) return false;
  if (!isCoastal(g, reg)) return false; // a port needs a shoreline, no inland harbors
  return affordable(g.factions[fid]!.stock, harborCost(g, reg, fid)); // any coastal tile may have its own Harbor (each +1 army cap)
}

export function startHarbor(g: GameState, regionId: RegionId, fid: FactionId): boolean {
  if (!canBuildHarbor(g, regionId, fid)) return false;
  const reg = g.regions[regionId]!;
  const cost = harborCost(g, reg, fid);
  pay(g.factions[fid]!.stock, cost);
  const ht = prodTurns(g, fid, HARBOR_TURNS);
  reg.production = { kind: 'building', building: BUILDING.Harbor, turnsLeft: ht, turnsTotal: ht, cost };
  return true;
}

// --- build a Barracks: the mainland's muster ground. Land maps have no sea, so the Harbor (and the +1
// army cap each port grants) is unavailable; the Barracks takes over exactly that supply role. Same
// price and build time as a Harbor, buildable on any owned land tile, one per tile. Isles maps keep
// the Harbor and cannot build Barracks, so each map kind has ONE distinct supply building. ---
export const barracksCost = (g: GameState, region: Region, fid: FactionId): number[] => expansionTax(reduceCost(islandCost(g, region, HARBOR_AMOUNT), g.factions[fid]!), g, fid, BUILDING.Barracks);

export function canBuildBarracks(g: GameState, regionId: RegionId, fid: FactionId): boolean {
  const reg = g.regions[regionId];
  if (!reg || reg.owner !== fid || reg.production || reg.buildings.includes(BUILDING.Barracks)) return false;
  return affordable(g.factions[fid]!.stock, barracksCost(g, reg, fid));
}

export function startBarracks(g: GameState, regionId: RegionId, fid: FactionId): boolean {
  if (!canBuildBarracks(g, regionId, fid)) return false;
  const reg = g.regions[regionId]!;
  const cost = barracksCost(g, reg, fid);
  pay(g.factions[fid]!.stock, cost);
  const bt = prodTurns(g, fid, HARBOR_TURNS);
  reg.production = { kind: 'building', building: BUILDING.Barracks, turnsLeft: bt, turnsTotal: bt, cost };
  return true;
}

// --- units: train, move, combat ---
/** The two resources a faction's RACE trains its BASE units from (race-specific, not island-specific). */
const factionUnitRes = (g: GameState, fid: FactionId): readonly number[] =>
  RACES.find((r) => r.key === g.factions[fid]?.race)?.unitRes ?? g.islands[fid]?.resources ?? [];

/** The extra resource a faction's RACE additionally needs for its tech-unlocked (advanced) units. */
const factionAdvancedRes = (g: GameState, fid: FactionId): number =>
  RACES.find((r) => r.key === g.factions[fid]?.race)?.advancedRes ?? GOLD;

/** Warrior(0) & Spearman(3) are trainable from the start; Archer(1) & Rider(2) each need the tech that
 *  unlocks them first (mil1 → Archer, mil2 → Rider; Rider is the deepest, last unlock). See TECHS.unlocks. */
export function unitUnlocked(g: GameState, fid: FactionId, type: UnitType): boolean {
  if (type === 0 || type === 3) return true;
  return g.factions[fid]!.techs.some((id) => techById(id)?.unlocks === type);
}

/** Cost to train a `type` unit for `fid`: UNIT_AMOUNT of each of its race's signature pair (Masonry-reduced),
 *  PLUS, for the tech-unlocked Archer/Rider, ADVANCED_AMOUNT of the race's distinct advanced resource. */
export const unitCost = (g: GameState, fid: FactionId, type: UnitType): number[] => {
  const cost = new Array<number>(RESOURCES).fill(0);
  // RECRUITMENT FATIGUE: every 3 soldiers a realm already fields (production, reserves and boats
  // included) raise the next recruit'''s signature-pair price by +1. The opening is untouched (0-2
  // units = base price); a grown realm pays real money for real armies, which is what finally makes
  // mid-game income a BUDGET again (measured: without this, 4 of 5 rounds had a stock rotting at the
  // 40 cap and every purchase was a free click; see scripts/ecotest.ts).
  const fatigue = Math.floor(unitCount(g, fid) / 3);
  for (const r of factionUnitRes(g, fid)) cost[r] = UNIT_AMOUNT + fatigue;
  if (type === 1 || type === 2) {
    const adv = factionAdvancedRes(g, fid);
    cost[adv] = (cost[adv] ?? 0) + ADVANCED_AMOUNT;
  }
  return reduceCost(cost, g.factions[fid]!);
};

/** How many supply buildings a faction owns (each raises its army cap by 1). ONLY the Barracks does
 *  that now: the Harbor pays gold and opens crossings instead, so ports and armies are separate calls. */
export const harborCount = (g: GameState, fid: FactionId): number =>
  g.regions.filter((r) => r.owner === fid && r.buildings.includes(BUILDING.Barracks)).length;

const REGIONS_PER_CAP = 3; // army supply also scales with empire: +1 cap per this many regions held
/** Crowns a realm holds: its own capital plus every rival seat it has conquered. */
export const capitalsHeld = (g: GameState, fid: FactionId): number =>
  g.regions.filter((r) => r.isCapital && r.owner === fid).length;

/** Army supply cap: the most units a faction may field at once: BASE_UNIT_CAP, plus one per supply
 *  building (Harbor / Barracks), plus one per REGIONS_PER_CAP regions. Tying it to territory means a
 *  growing economy can field a growing army, while the modest ratio keeps the map from flooding.
 *  Under the free-play CROWN RULE, capitals matter too: every crown beyond the first feeds the war
 *  machine (+1 cap each), and a realm holding NO capital is a kingdom in exile (-2 cap, never below 1). */
export const unitCap = (g: GameState, fid: FactionId): number => {
  const regions = g.regions.filter((r) => r.owner === fid).length;
  let cap = BASE_UNIT_CAP + Math.floor(regions / REGIONS_PER_CAP) + harborCount(g, fid);
  if (g.capitalRule) {
    const crowns = capitalsHeld(g, fid);
    cap += Math.max(0, crowns - 1);
    if (crowns === 0) cap = Math.max(1, cap - 2);
  }
  return cap;
};

/** Campaign handicaps (see types.Handicap). Absent in free play, so every read is a no-op there. */
const hcap = (g: GameState, fid: FactionId): Handicap | undefined => g.factions[fid]?.handicap;
/** Build time under a handicap: a slowed realm takes proportionally longer, never less than one turn. */
const prodTurns = (g: GameState, fid: FactionId, base: number): number => Math.max(1, Math.round(base * (hcap(g, fid)?.buildMul ?? 1)));

/** How many units a faction currently has toward its cap: fielded units + units still in production
 *  + units at sea in boats. Counted on independent passes so a tile that BOTH holds a unit and is
 *  producing one (a fielded unit repositioned onto a still-building tile) contributes 2, not 1,
 *  otherwise the cap is bypassable. */
export const unitCount = (g: GameState, fid: FactionId): number =>
  g.regions.filter((r) => r.unit && r.unit.owner === fid).length +
  g.regions.filter((r) => r.owner === fid && r.production?.kind === 'unit').length +
  g.regions.filter((r) => r.owner === fid && r.reserve != null).length + // paid for, mustering out shortly
  g.voyages.filter((v) => v.owner === fid).length;

/** Troops are raised at a MUSTER GROUND: a Barracks, or the capital (the seat of power keeps its own
 *  garrison yard, so a fresh realm can always field its first soldiers). Everything else is countryside. */
export const canMuster = (reg: Region): boolean => reg.isCapital || reg.buildings.includes(BUILDING.Barracks);

export function canTrain(g: GameState, regionId: RegionId, fid: FactionId, type: UnitType): boolean {
  const reg = g.regions[regionId];
  if (!reg || reg.owner !== fid || reg.production) return false;
  // A muster ground trains even with a soldier standing on it: the recruit waits in the yard as a RESERVE and
  // musters out once the tile is clear. Only one may wait at a time, so a full yard blocks the next order.
  if (reg.reserve != null) return false;
  if (!canMuster(reg)) return false; // no muster ground here: build a Barracks first
  if (hcap(g, fid)?.lockUnits?.includes(type)) return false; // campaign: this realm never fields these
  if (!unitUnlocked(g, fid, type)) return false; // Archer/Rider need their unlock tech researched first
  if (unitCount(g, fid) >= unitCap(g, fid)) return false; // army supply cap (raise it by building Harbors)
  return affordable(g.factions[fid]!.stock, unitCost(g, fid, type));
}

export function startTrain(g: GameState, regionId: RegionId, fid: FactionId, unitType: UnitType): boolean {
  if (!canTrain(g, regionId, fid, unitType)) return false;
  const reg = g.regions[regionId]!;
  const cost = unitCost(g, fid, unitType);
  pay(g.factions[fid]!.stock, cost);
  const turns = prodTurns(g, fid, UNIT_TURNS[unitType] ?? 2);
  reg.production = { kind: 'unit', unitType, turnsLeft: turns, turnsTotal: turns, cost };
  return true;
}

const unitRange = (type: UnitType): number => (type === 1 ? ARCHER_RANGE : 1); // the Archer strikes from afar

/** An Archer's arrow is free of its feet: one shot a round, spent independently of move points. */
export const hasArrow = (u: Unit): boolean => unitRange(u.type) >= 2 && !u.shot;
/** Does this unit still have ANY action left this round: a step to take, or an arrow to loose. */
export const canStillAct = (u: Unit): boolean => u.movesLeft > 0 || hasArrow(u);

// Last stand: when the HUMAN is squeezed to very few regions, their defenders take less damage, so a cornered
// player gets a comeback window instead of being wiped in one turn. Player-only in SOLO on purpose: a symmetric
// shield just protects the AIs from being finished off too, which lengthens games and cuts the player's own wins
// (measured). The shield grows the closer the player is to elimination. In MULTIPLAYER the human perks would be
// unfair between humans, so MP_RULES below decides the symmetric policy (validated by headless simulation).
export const LAST_STAND_SOFT = 4; // the UI "besieged" line uses this (Normal's threshold; Hard's is tighter, see DIFF.lastStand*)
/** The difficulty's actual shield threshold, for UI that must not promise a buff Hard doesn't give. */
export const lastStandSoftFor = (diff: Difficulty): number => DIFF[diff].lastStandSoft;
const lastStandReduce = (defRegions: number, diff: Difficulty): number => {
  const d = DIFF[diff];
  return defRegions <= d.lastStandHard ? 2 : defRegions <= d.lastStandSoft ? 1 : 0;
};
export const regionCountOf = (g: GameState, fid: FactionId): number => g.regions.reduce((n, r) => (r.owner === fid ? n + 1 : n), 0);

// Multiplayer symmetric-balance policy. In 'mp' mode NO faction gets the solo human perks; these knobs say
// which of those mechanics instead apply to EVERYONE (true) or NO ONE (false). Defaults were chosen from
// headless simulations (scripts/balancesim.ts, 1200 all-AI mp games per variant across 3/4/5 seats):
//   sameTurnStrike TRUE  → wins by turn-order slot spread 14.3pp (vs 34.8-38.4pp when no one may strike:
//                          the one-turn mobilisation wave syncs to round boundaries and hands the round
//                          OPENER a huge edge - slot 0 won ~50% of games; allowing everyone halves it).
//   lastStandAll   TRUE  → best fairness of the four variants and keeps the comeback window for humans
//                          (costs some game length: more round-cap finishes in AI-only play, acceptable).
// Mutated ONLY by the balance sim to compare variants; in production these are fixed constants per build
// (identical on every client of the same version, so the lockstep sim stays deterministic).
export const MP_RULES = {
  sameTurnStrike: true, // anyone may attack on the turn they declare war (symmetric; fairest per sim)
  lastStandAll: true, // the besieged comeback shield protects every HUMAN seat equally (see shielded below)
};

/** Does the last-stand shield protect this defender in this game? Solo: the human only. MP: every HUMAN
 *  seat equally (the fairness the sim picked), but NOT the AI-filled seats: early game every realm is
 *  small enough to qualify, and shielded AI fill made enemy warriors read as unkillable while their own
 *  tech-boosted hits landed full (player report). Bots need no comeback story; people do. */
const shielded = (g: GameState, defFac: Faction): boolean => (g.mode === 'mp' ? MP_RULES.lastStandAll && defFac.isPlayer : defFac.isPlayer);

/** Damage a single hit from `atk` deals to `def` on `defReg` (always ≥ 1). Pure. `defRegions` = how many
 *  regions the defender's faction still holds, for the last-stand shield (pass Infinity to skip it).
 *  `shieldOn` = whether the shield applies to this defender at all (see `shielded`; solo: humans only). */
export function attackDamage(atk: Unit, atkFac: Faction, def: Unit, defReg: Region, defFac: Faction, isSea: boolean, defRegions = Infinity, diff: Difficulty = 'normal', shieldOn?: boolean, isRanged = false): number {
  let dmg = BASE_DMG;
  if (beats(atk.type, def.type)) dmg += RPS_DMG; // rock-paper-scissors bonus
  dmg += atkDmgBonus(atkFac);
  dmg -= defDmgReduce(defFac);
  dmg -= watchtowerReduce(defFac);
  const siege = has(atkFac, 'sie3'); // siege engines flatten fortifications: walls, gates and capitals alike
  if ((defReg.buildings.includes(BUILDING.Wall) || defReg.terrain === 'gate') && !siege) dmg -= wallDmgReduce(defFac);
  if (defReg.isCapital && !siege) dmg -= CAPITAL_DMG_REDUCE;
  // a muster ground is a fortified camp: its palisade and drill shave a point off blows landed there
  if (defReg.buildings.includes(BUILDING.Barracks) && !siege) dmg -= BARRACKS_DMG_REDUCE;
  if (isSea && !has(atkFac, 'mil3')) dmg -= SEA_DMG_PENALTY;
  if (isRanged) dmg -= RANGED_DMG_PENALTY; // an arrow is lighter than a blade (see RANGED_DMG_PENALTY)
  if (shieldOn ?? defFac.isPlayer) dmg -= lastStandReduce(defRegions, diff); // a besieged defender is tougher to finish off (comeback window)
  return Math.max(1, Math.min(MAX_HP, dmg));
}

export const SEA_RANGE = 7; // mainland crossings: how far along the coast a boat may carry troops (exported for the UI's blocked-crossing hint)

/** Every coastal tile a boat launched from `port` could reach (ownership rules applied by the caller):
 *  sea-linked islands on an archipelago, other shores of the same continent within SEA_RANGE on land. */
function seaReach(g: GameState, port: Region): Region[] {
  const out: Region[] = [];
  const links = g.islands[port.island]?.seaLinks;
  for (const reg of g.regions) {
    if (reg.id === port.id || !isCoastal(g, reg) || impassable(reg.terrain)) continue;
    if (g.mapKind === 'land') {
      if (hexDistance(port, reg) > SEA_RANGE) continue;
      if (port.neighbors.includes(reg.id)) continue; // a neighbour is a march, not a crossing
      if (!sharesWater(g, port, reg)) continue; // a boat never crosses land: same sea or same lake only
    } else {
      if (reg.island === port.island) continue;
      if (links && !links.includes(reg.island)) continue;
    }
    out.push(reg);
  }
  return out;
}

export interface UnitTargets {
  move: RegionId[]; // step into an empty tile (reposition, claim neutral, or occupy a cleared tile)
  melee: RegionId[]; // adjacent enemy unit → melee (defender retaliates)
  ranged: RegionId[]; // Archer only: enemy unit 2 tiles off on the same island (retaliation only if it's ranged too)
  sea: RegionId[]; // sea-linked enemy region → amphibious assault (empty = capture, occupied = fight)
}

export function unitTargets(g: GameState, fromId: RegionId): UnitTargets {
  const move: RegionId[] = [];
  const melee: RegionId[] = [];
  const ranged: RegionId[] = [];
  const sea: RegionId[] = [];
  const from = g.regions[fromId];
  if (!from || !from.unit) return { move, melee, ranged, sea };
  // A bow and a pair of boots are separate: an Archer looses ONE arrow a round and that arrow costs no
  // movement, so it may march then shoot, or shoot then march. Everything else (stepping, closing to melee,
  // boarding a boat) still spends move points, so those lists stay gated on them.
  const canStep = from.unit.movesLeft > 0;
  const canLoose = unitRange(from.unit.type) >= 2 && !from.unit.shot;
  if (!canStep && !canLoose) return { move, melee, ranged, sea };
  const owner = from.unit.owner;
  // you may only attack / invade a faction you have DECLARED WAR on (neutral land is always free to take),
  // AND not on the same turn war was declared: armies mobilise first, so a fresh war can't strike until next
  // turn. This stops an AI from declaring war and sacking your capital in one uninterrupted turn.
  // SOLO: the "armies mobilise first" grace applies only to AI aggressors; the HUMAN may strike the same turn
  // they declare, so "declare war, then attack" works as the player expects.
  // MP: symmetric, per MP_RULES.sameTurnStrike (everyone may, or no one may) — chosen by headless simulation.
  const strikeOnDeclareTurn = g.mode === 'mp' ? MP_RULES.sameTurnStrike : owner === g.playerFaction;
  const warAged = (o: FactionId): boolean => strikeOnDeclareTurn || g.warStamp[o < owner ? `${o}-${owner}` : `${owner}-${o}`] !== g.round;
  const hostile = (o: FactionId): boolean => o !== owner && o >= 0 && atWar(g, owner, o) && warAged(o);
  for (const n of canStep ? from.neighbors : []) {
    const reg = g.regions[n]!;
    if (!reg.unit) {
      if (reg.owner === owner || reg.owner === NEUTRAL) move.push(n); // reposition or claim-move
      else if (hostile(reg.owner)) move.push(n); // occupy a cleared enemy tile
    } else if (hostile(reg.owner)) {
      melee.push(n);
    }
  }
  // (Riders no longer leap two tiles in one move; they instead get TWO move points a turn, so they cover the same
  // ground in two single-tile hops, or move-then-charge. See unitMoves + how moveOrAttack spends the points.)
  if (canLoose) {
    for (const reg of g.regions) {
      // one landmass = one battlefield: only an archipelago's water stops an arrow, and there
      // "another island" really is out of reach. On land the province id means nothing to a bowman.
      if ((g.mapKind !== 'land' && reg.island !== from.island) || !reg.unit || !hostile(reg.owner)) continue;
      if (hexDistance(from, reg) === 2) ranged.push(reg.id);
    }
  }
  // Sea crossing: a unit sails only from a tile that HOLDS a Harbor (the port is the gangway, not the
  // whole island), and lands on a COASTAL tile: an amphibious force hits a beach, never a landlocked
  // interior tile. An enemy shore needs a declared war; an empty neutral or own shore is free, so a
  // fleet ferries troops as well as invading.
  if (canStep && from.buildings.includes(BUILDING.Harbor)) {
    for (const reg of seaReach(g, from)) {
      if (hostile(reg.owner) || ((reg.owner === NEUTRAL || reg.owner === owner) && !reg.unit)) sea.push(reg.id);
    }
  }
  return { move, melee, ranged, sea };
}

function updateEliminations(g: GameState): void {
  const counts = regionCounts(g);
  for (const f of g.factions) {
    if (f.eliminated || (counts[f.id] ?? 0) > 0) continue;
    f.eliminated = true;
    g.alliances = g.alliances.filter(([a, b]) => a !== f.id && b !== f.id);
    g.wars = g.wars.filter(([a, b]) => a !== f.id && b !== f.id);
    g.offers = g.offers.filter((o) => o.from !== f.id);
    // its fleet goes down with it: a boat with no realm left to land for would otherwise drift at sea
    // forever (its owner's turn never comes again, so nothing would ever resolve or clear it)
    g.voyages = g.voyages.filter((v) => v.owner !== f.id);
  }
}

export type CombatKind = 'moved' | 'captured' | 'melee' | 'ranged' | 'sea' | 'sail'; // 'sail': the unit boarded a boat (isles); it lands next turn
export interface CombatResult {
  kind: CombatKind;
  fromId: RegionId;
  toId: RegionId;
  attackerType: UnitType;
  attackerOwner: FactionId;
  defenderType: UnitType | null;
  attackerDied: boolean;
  defenderDied: boolean;
  captured: boolean; // region ownership passed to the attacker
  retaliated: boolean;
  attackerHp: number; // attacker HP afterwards (0 if it died)
  defenderHp: number; // defender HP afterwards (0 if it died / none)
}

/** Move the unit or resolve HP combat. Returns a full result (for UI/animation), or null if illegal. */
/** Capturing an enemy capital raids a share of that faction's whole stockpile, a one-time windfall for the
 *  raider (and a real blow to the loser). No-op for neutral land or re-taking your own capital. */
function plunderCapital(g: GameState, capturer: FactionId, formerOwner: FactionId): void {
  if (formerOwner < 0 || formerOwner === capturer) return;
  const victim = g.factions[formerOwner];
  const raider = g.factions[capturer];
  if (!victim || !raider) return;
  for (let r = 0; r < RESOURCES; r++) {
    const take = Math.floor((victim.stock[r] ?? 0) * PLUNDER_SHARE);
    if (take > 0) {
      victim.stock[r]! -= take;
      raider.stock[r]! += take;
    }
  }
}

// running per-game tallies (fed to the cross-game achievement profile at game end). Defensive against a sparse
// array from an older save so a resumed game never crashes on a missing row.
function bumpCapture(g: GameState, fid: FactionId): void {
  g.stats.captured[fid] = (g.stats.captured[fid] ?? 0) + 1;
}
function bumpKill(g: GameState, fid: FactionId, type: UnitType): void {
  const row = (g.stats.kills[fid] ??= [0, 0, 0, 0]);
  row[type] = (row[type] ?? 0) + 1;
}
function bumpBuilt(g: GameState, fid: FactionId, type: UnitType): void {
  const row = (g.stats.built[fid] ??= [0, 0, 0, 0]);
  row[type] = (row[type] ?? 0) + 1;
}

export function moveOrAttack(g: GameState, fromId: RegionId, toId: RegionId, fid: FactionId): CombatResult | null {
  const from = g.regions[fromId];
  const to = g.regions[toId];
  if (!from || !to || !from.unit || from.unit.owner !== fid) return null;
  const t = unitTargets(g, fromId); // (already empty when the unit has neither a step nor an arrow left)
  const isMove = t.move.includes(toId);
  const isMelee = t.melee.includes(toId);
  const isRanged = t.ranged.includes(toId);
  const isSea = t.sea.includes(toId);
  if (!isMove && !isMelee && !isRanged && !isSea) return null;

  const attacker = from.unit;
  // move points left after this action: a plain LAND move spends one (a Rider can then act again); a MELEE or a
  // SEA crossing is committal and spends every remaining point (so a Rider's overseas hop lands it at 0/2). An
  // ARROW spends no movement at all, only the archer's one shot for the round.
  const remain = isRanged ? attacker.movesLeft : isMove ? attacker.movesLeft - 1 : 0;
  if (isRanged) attacker.shot = true;
  const base = { fromId, toId, attackerType: attacker.type, attackerOwner: fid, retaliated: false, attackerDied: false };

  // sea crossing: no more teleporting armies. The unit BOARDS a boat and waits at sea beside its tile,
  // committed to the chosen beach; the landing (with the normal amphibious rules) resolves at the start
  // of its owner's next turn via resolveVoyages. Boarding spends the unit's whole turn.
  if (isSea) {
    g.voyages.push({ unit: { ...attacker, movesLeft: 0 }, owner: fid, from: fromId, dest: toId });
    from.unit = null;
    return { ...base, kind: 'sail', defenderType: null, defenderDied: false, captured: false, attackerHp: attacker.hp, defenderHp: 0 };
  }

  // plain move / capture into an empty tile (adjacent land, or amphibious onto an empty enemy tile)
  if (!to.unit) {
    const prev = to.owner;
    const captured = prev !== fid;
    to.unit = { ...attacker, movesLeft: remain };
    from.unit = null;
    to.owner = fid;
    if (captured) {
      // the storming wrecks whatever was on the stocks here and scatters the recruits waiting in the yard.
      // ONLY on a real change of hands: marching your own soldier home through your own town must not
      // wreck that town's own build queue.
      to.production = null;
      to.reserve = null;
    }
    if (captured) bumpCapture(g, fid); // gained a tile you didn't hold (neutral claim or enemy capture)
    if (captured && to.isCapital) plunderCapital(g, fid, prev); // sacking an enemy capital raids its treasury
    updateEliminations(g);
    return { ...base, kind: captured ? 'captured' : 'moved', defenderType: null, defenderDied: false, captured, attackerHp: attacker.hp, defenderHp: 0 };
  }

  // combat
  const defender = to.unit;
  const defOwner = to.owner; // remember who held the tile, so a kill-capture of a capital can plunder them
  const atkFac = g.factions[fid]!;
  const defFac = g.factions[to.owner]!;
  const kind: CombatKind = isRanged ? 'ranged' : isSea ? 'sea' : 'melee';
  const dealt = attackDamage(attacker, atkFac, defender, to, defFac, isSea, regionCountOf(g, defOwner), g.difficulty, shielded(g, defFac), isRanged);

  if (defender.hp - dealt <= 0) {
    // defender destroyed
    to.unit = null;
    attacker.movesLeft = remain;
    bumpKill(g, fid, defender.type); // tally the slain enemy by type
    if (kind === 'ranged') {
      // ranged kill: tile emptied but the archer holds its ground; region stays enemy-owned until occupied.
      // A BOWMAN shot down by another bowman still looses its own arrow as it falls: two archers in range of
      // each other trade volleys, so opening a duel is never a free kill. Nothing else reaches the shooter.
      let retaliated = false;
      if (hexDistance(from, to) <= unitRange(defender.type)) {
        retaliated = true;
        const back = Math.max(1, attackDamage(defender, defFac, attacker, from, atkFac, false, regionCountOf(g, fid), g.difficulty, shielded(g, atkFac), true) - RETALIATION_PENALTY);
        if (attacker.hp - back <= 0) {
          from.unit = null; // both bowmen fall, arrows crossing in the air
          updateEliminations(g);
          return { ...base, kind, defenderType: defender.type, defenderDied: true, captured: false, retaliated: true, attackerDied: true, attackerHp: 0, defenderHp: 0 };
        }
        attacker.hp -= back;
      }
      return { ...base, kind, defenderType: defender.type, defenderDied: true, captured: false, retaliated, attackerHp: attacker.hp, defenderHp: 0 };
    }
    // melee / sea kill. A defender never dies without a fight: as it falls it lands a last-stand counter-blow
    // on the attacker if the attacker is within reach (dist 1 for a beach landing, always true for melee/sea).
    // So a close assault is a two-way clash, and a reckless charge (or amphibious landing) can get the attacker
    // cut down alongside the defender it kills. Ranged kills draw no counter (that's the archer's whole point).
    const closeDist = isSea ? 1 : hexDistance(from, to);
    let retaliated = false;
    if (closeDist <= unitRange(defender.type)) {
      retaliated = true;
      const back = Math.max(1, attackDamage(defender, defFac, attacker, from, atkFac, false, regionCountOf(g, fid), g.difficulty, shielded(g, atkFac)) - RETALIATION_PENALTY);
      if (attacker.hp - back <= 0) {
        // mutual destruction: the attacker is cut down by the parting blow. The tile is cleared but NOT taken.
        from.unit = null;
        updateEliminations(g);
        return { ...base, kind, defenderType: defender.type, defenderDied: true, captured: false, retaliated: true, attackerDied: true, attackerHp: 0, defenderHp: 0 };
      }
      attacker.hp -= back; // the assault succeeds but the attacker is bloodied
    }
    // the attacker survives the parting blow (or drew none) and storms the cleared tile
    to.unit = { ...attacker, movesLeft: remain };
    from.unit = null;
    to.owner = fid;
    to.production = null; // the storming wrecks whatever was on the stocks here
    to.reserve = null; // and scatters the recruits waiting in the yard
    bumpCapture(g, fid); // storming the cleared tile is also a capture
    if (to.isCapital) plunderCapital(g, fid, defOwner); // storming an enemy capital raids its treasury
    updateEliminations(g);
    return { ...base, kind, defenderType: defender.type, defenderDied: true, captured: true, retaliated, attackerHp: attacker.hp, defenderHp: 0 };
  }

  // defender survives → take the hit, then retaliate if it can reach the attacker
  defender.hp -= dealt;
  attacker.movesLeft = remain;
  let attackerHp = attacker.hp;
  let retaliated = false;
  let attackerDied = false;
  const dist = isSea ? 1 : hexDistance(from, to);
  if (dist <= unitRange(defender.type)) {
    retaliated = true;
    const back = Math.max(1, attackDamage(defender, defFac, attacker, from, atkFac, false, regionCountOf(g, fid), g.difficulty, shielded(g, atkFac), dist > 1) - RETALIATION_PENALTY); // counter is weaker than a full strike
    attackerHp = attacker.hp - back;
    if (attackerHp <= 0) {
      from.unit = null;
      attackerDied = true;
      attackerHp = 0;
    } else {
      attacker.hp = attackerHp;
    }
  }
  return { ...base, kind, defenderType: defender.type, defenderDied: false, captured: false, retaliated, attackerDied, attackerHp, defenderHp: defender.hp };
}

// --- sea voyages: the delayed half of a sea crossing (see the 'sail' branch in moveOrAttack) ---

/** Resolve every boat belonging to `fid` at the start of its turn, in boarding order. Deterministic:
 *  called from the turn advance on every client at the same seam, so lockstep stays in sync. */
export function resolveVoyages(g: GameState, fid: FactionId): void {
  if (!g.voyages.length) return;
  const keep: Voyage[] = [];
  for (const v of g.voyages) {
    if (v.owner !== fid || g.factions[fid]?.eliminated) {
      if (!g.factions[v.owner]?.eliminated) keep.push(v); // a fallen realm's boats sink with it
      continue;
    }
    if (!landVoyage(g, v)) keep.push(v); // couldn't land anywhere: stay at sea, try again next turn
  }
  // landings can eliminate factions mid-loop; sink any boats orphaned by that too
  g.voyages = keep.filter((v) => !g.factions[v.owner]?.eliminated);
}

/** May `owner` put troops ashore on this tile? (An occupied tile needs a declared war; empty land may be
 *  neutral, their own, or an enemy's.) Shared by the landing itself and by the re-route target list. */
function canLandOn(g: GameState, owner: FactionId, reg: Region): boolean {
  const hostile = (o: FactionId): boolean => o >= 0 && o !== owner && atWar(g, owner, o);
  return !impassable(reg.terrain) && (reg.unit ? hostile(reg.owner) : reg.owner === NEUTRAL || reg.owner === owner || hostile(reg.owner));
}

/** Beaches a boat at sea may be sent to: any coastal tile it could have sailed to from its origin
 *  (sea-linked islands), plus free coastal tiles of its OWN island, so calling the crossing off and
 *  putting the troops back ashore at home is always an option. */
export function voyageTargets(g: GameState, index: number): RegionId[] {
  const v = g.voyages[index];
  const home = v ? g.regions[v.from] : undefined;
  if (!v || !home) return [];
  const out = seaReach(g, home).filter((reg) => canLandOn(g, v.owner, reg)).map((reg) => reg.id);
  if (!home.unit && (home.owner === v.owner || home.owner === NEUTRAL)) out.push(home.id); // call the crossing off: land back at the port
  return out;
}

/** What a re-routed crossing did, so the UI can animate it (a landing, or an assault that was thrown back). */
export interface VoyageResult {
  landed: boolean; // false = repelled, the boat still holds station
  to: RegionId;
  captured: boolean;
  defenderDied: boolean;
  attackerDied: boolean;
  attackerHp: number;
  defenderHp: number;
}

/** Send a STALLED boat to a new beach and resolve the landing immediately: the crossing already cost
 *  its turn, so re-aiming it is a normal action, not another turn of waiting. */
export function rerouteVoyage(g: GameState, fid: FactionId, index: number, toId: RegionId, keepReplay = false): VoyageResult | null {
  const v = g.voyages[index];
  if (!v || v.owner !== fid || !v.stalled) return null;
  if (!voyageTargets(g, index).includes(toId)) return null;
  v.dest = toId;
  v.stalled = false;
  const mark = g.replay.length;
  const landed = landVoyage(g, v);
  const ev = g.replay[mark]; // landVoyage records the landing
  if (!keepReplay) g.replay.length = mark; // a human caller animates it itself; an AI's must stay for the replay
  if (landed) g.voyages = g.voyages.filter((x) => x !== v);
  if (!ev || ev.kind !== 'unit') return { landed, to: toId, captured: false, defenderDied: false, attackerDied: false, attackerHp: v.unit.hp, defenderHp: 0 };
  return { landed, to: ev.to, captured: ev.captured, defenderDied: ev.defenderDied, attackerDied: ev.attackerDied, attackerHp: ev.attackerHp, defenderHp: ev.defenderHp };
}

/** Try to land one boat. Returns false if the unit stays at sea (repelled, or nowhere to land). */
function landVoyage(g: GameState, v: Voyage): boolean {
  const unit: Unit = { ...v.unit, movesLeft: 0 };
  const sail = (to: RegionId, captured: boolean, defenderDied: boolean, attackerDied: boolean, attackerHp: number, defenderHp: number): void => {
    g.replay.push({ kind: 'unit', faction: v.owner, unitType: unit.type, from: v.from, to, combat: 'sea', captured, defenderDied, attackerDied, attackerHp, defenderHp });
  };

  // the player's own fleet reports back: a crossing that does not go to plan must never be silent
  const note = (text: string): void => {
    if (g.factions[v.owner]?.isPlayer) g.log.push({ kind: 'info', text });
  };
  const dest = g.regions[v.dest]!;
  // a boat never crosses land, even one boarded under the OLD distance-only rule (a resumed save):
  // such a voyage stalls exactly like a blocked beach, and re-aiming it only offers legal shores
  const overland = g.mapKind === 'land' && v.dest !== v.from && !sharesWater(g, g.regions[v.from]!, dest);
  if (!canLandOn(g, v.owner, dest) || overland) {
    // The committed beach became illegal while the boat was at sea: a rival claimed it in peacetime,
    // an ally moved in, peace was signed. The fleet does NOT pick a consolation beach on its own and
    // it does not force a landing it has no right to: it holds station, and its owner re-aims it (a
    // stalled boat can be selected and sent to any legal shore, landing there the same turn).
    v.stalled = true;
    note('⛵ Your landing was blocked. The boat holds station: select it and pick another shore, or declare war.');
    return false;
  }
  v.stalled = false; // a landing (or an assault) is happening

  // undefended shore: land and take it (mirrors moveOrAttack's empty-tile path)
  if (!dest.unit) {
    const prev = dest.owner;
    const captured = prev !== v.owner;
    dest.unit = unit;
    dest.owner = v.owner;
    if (captured) {
      dest.production = null; // a landing wrecks the works too (again, only when the shore changes hands:
      dest.reserve = null; // ferrying troops home to your own beach leaves your own works standing)
    }
    if (captured) bumpCapture(g, v.owner);
    if (captured && dest.isCapital) plunderCapital(g, v.owner, prev);
    updateEliminations(g);
    sail(dest.id, captured, false, false, unit.hp, 0);
    return true;
  }

  // defended beach: amphibious assault with the normal sea-attack rules
  const defender = dest.unit;
  const defOwner = dest.owner;
  const atkFac = g.factions[v.owner]!;
  const defFac = g.factions[defOwner]!;
  const dealt = attackDamage(unit, atkFac, defender, dest, defFac, true, regionCountOf(g, defOwner), g.difficulty, shielded(g, defFac));
  const counter = (): number =>
    Math.max(1, attackDamage(defender, defFac, unit, g.regions[v.from]!, atkFac, false, regionCountOf(g, v.owner), g.difficulty, shielded(g, atkFac)) - RETALIATION_PENALTY);

  if (defender.hp - dealt <= 0) {
    // defender falls; a beach landing is always in reach of its parting blow (dist 1)
    dest.unit = null;
    bumpKill(g, v.owner, defender.type);
    let attackerDied = false;
    if (1 <= unitRange(defender.type)) {
      const back = counter();
      if (unit.hp - back <= 0) attackerDied = true;
      else unit.hp -= back;
    }
    if (!attackerDied) {
      dest.unit = unit;
      dest.owner = v.owner;
      dest.production = null; // a landing wrecks the works too
      dest.reserve = null;
      bumpCapture(g, v.owner);
      if (dest.isCapital) plunderCapital(g, v.owner, defOwner);
    }
    updateEliminations(g);
    sail(dest.id, !attackerDied, true, attackerDied, attackerDied ? 0 : unit.hp, 0);
    return true;
  }

  // defender holds the beach: it takes the hit and counters; a surviving attacker stays OFFSHORE in its
  // boat (a landing was repelled, not a retreat) and assaults again at its next turn start
  defender.hp -= dealt;
  let attackerDied = false;
  if (1 <= unitRange(defender.type)) {
    const back = counter();
    if (unit.hp - back <= 0) attackerDied = true;
    else unit.hp -= back;
  }
  sail(dest.id, false, false, attackerDied, attackerDied ? 0 : unit.hp, defender.hp);
  if (attackerDied) return true; // lost at sea: the voyage is over
  v.unit = unit; // remember the wounds; the boat keeps its post
  v.dest = dest.id;
  v.stalled = true; // thrown back: its owner may re-aim it (or leave it to assault the same beach again)
  return false;
}

// The pre-attack forecast: what moveOrAttack(from → to) WOULD do, computed without touching state.
export interface CombatPreview {
  kind: CombatKind;
  attackerType: UnitType;
  defenderType: UnitType | null; // null = a bloodless move/capture into an empty tile
  attackerHpBefore: number;
  attackerHpAfter: number;
  defenderHpBefore: number;
  defenderHpAfter: number;
  attackerDies: boolean;
  defenderDies: boolean;
  captures: boolean; // will the attacker end up owning the tile?
  dealt: number; // damage the attacker lands (0 for a bloodless move)
  retaliation: number | null; // counter damage the attacker takes (null = no counter reaches it, e.g. ranged)
  rps: 'strong' | 'weak' | 'even'; // the attacker's matchup vs the defender, from the attacker's point of view
  isCapital: boolean; // target is a capital (tougher defence + plunder on capture)
}

/** Resolve the OUTCOME of moveOrAttack(fromId → toId) WITHOUT mutating anything. Returns null if the move is
 *  illegal. A pure mirror of moveOrAttack so the UI can show a truthful pre-attack forecast (same modifiers,
 *  same retaliation rules). Keep in lockstep with moveOrAttack if the combat maths change. */
export function previewCombat(g: GameState, fromId: RegionId, toId: RegionId, fid: FactionId): CombatPreview | null {
  const from = g.regions[fromId];
  const to = g.regions[toId];
  if (!from || !to || !from.unit || from.unit.owner !== fid) return null;
  const t = unitTargets(g, fromId);
  const isMelee = t.melee.includes(toId);
  const isRanged = t.ranged.includes(toId);
  const isSea = t.sea.includes(toId);
  if (!t.move.includes(toId) && !isMelee && !isRanged && !isSea) return null;

  const attacker = from.unit;
  const rpsOf = (a: UnitType, d: UnitType): 'strong' | 'weak' | 'even' => (beats(a, d) ? 'strong' : beats(d, a) ? 'weak' : 'even');

  // bloodless move / capture into an empty tile (mirrors the `!to.unit` branch of moveOrAttack)
  if (!to.unit) {
    const captured = to.owner !== fid;
    return {
      kind: captured ? 'captured' : 'moved',
      attackerType: attacker.type,
      defenderType: null,
      attackerHpBefore: attacker.hp,
      attackerHpAfter: attacker.hp,
      defenderHpBefore: 0,
      defenderHpAfter: 0,
      attackerDies: false,
      defenderDies: false,
      captures: captured,
      dealt: 0,
      retaliation: null,
      rps: 'even',
      isCapital: to.isCapital,
    };
  }

  const defender = to.unit;
  const atkFac = g.factions[fid]!;
  const defFac = g.factions[to.owner]!;
  const kind: CombatKind = isRanged ? 'ranged' : isSea ? 'sea' : 'melee';
  const dealt = attackDamage(attacker, atkFac, defender, to, defFac, isSea, regionCountOf(g, to.owner), g.difficulty, shielded(g, defFac), isRanged);
  const shared = {
    kind,
    attackerType: attacker.type,
    defenderType: defender.type,
    attackerHpBefore: attacker.hp,
    defenderHpBefore: defender.hp,
    dealt,
    rps: rpsOf(attacker.type, defender.type),
    isCapital: to.isCapital,
  };

  if (defender.hp - dealt <= 0) {
    // the defender dies. A ranged kill just clears the tile (no counter reaches the archer). A melee/sea kill
    // triggers the defender's last-stand counter-blow: the attacker takes it, storms the tile, and can even
    // fall alongside the defender (mutual destruction, tile cleared but not taken). Mirrors moveOrAttack.
    if (kind === 'ranged') {
      // mirrors moveOrAttack: only a fellow bowman answers a killing arrow, and it can take the shooter with it
      let volley: number | null = null;
      let hpAfter = attacker.hp;
      if (hexDistance(from, to) <= unitRange(defender.type)) {
        volley = Math.max(1, attackDamage(defender, defFac, attacker, from, atkFac, false, regionCountOf(g, fid), g.difficulty, shielded(g, atkFac), true) - RETALIATION_PENALTY);
        hpAfter = attacker.hp - volley;
      }
      const bothFall = hpAfter <= 0;
      return { ...shared, attackerHpAfter: bothFall ? 0 : hpAfter, defenderHpAfter: 0, attackerDies: bothFall, defenderDies: true, captures: false, retaliation: volley };
    }
    const closeDist = isSea ? 1 : hexDistance(from, to);
    let retaliation: number | null = null;
    let attackerHpAfter = attacker.hp;
    if (closeDist <= unitRange(defender.type)) {
      retaliation = Math.max(1, attackDamage(defender, defFac, attacker, from, atkFac, false, regionCountOf(g, fid), g.difficulty, shielded(g, atkFac)) - RETALIATION_PENALTY);
      attackerHpAfter = attacker.hp - retaliation;
    }
    const attackerDies = attackerHpAfter <= 0;
    return { ...shared, attackerHpAfter: attackerDies ? 0 : attackerHpAfter, defenderHpAfter: 0, attackerDies, defenderDies: true, captures: !attackerDies, retaliation };
  }

  // the defender survives, so it counter-attacks if the attacker is within its reach (ranged strikes dodge this)
  const dist = isSea ? 1 : hexDistance(from, to);
  let retaliation: number | null = null;
  let attackerHpAfter = attacker.hp;
  if (dist <= unitRange(defender.type)) {
    retaliation = Math.max(1, attackDamage(defender, defFac, attacker, from, atkFac, false, regionCountOf(g, fid), g.difficulty, shielded(g, atkFac), dist > 1) - RETALIATION_PENALTY);
    attackerHpAfter = attacker.hp - retaliation;
  }
  const attackerDies = attackerHpAfter <= 0;
  return { ...shared, attackerHpAfter: attackerDies ? 0 : attackerHpAfter, defenderHpAfter: defender.hp - dealt, attackerDies, defenderDies: false, captures: false, retaliation };
}

export function cancelProduction(g: GameState, regionId: RegionId, fid: FactionId): boolean {
  const reg = g.regions[regionId];
  if (!reg || reg.owner !== fid || !reg.production) return false;
  refund(g.factions[fid]!.stock, reg.production.cost);
  reg.production = null;
  return true;
}

// --- research ---
export const techById = (id: string): Tech | undefined => TECHS.find((t) => t.id === id);

export function canResearch(g: GameState, fid: FactionId, techId: string): boolean {
  if (hcap(g, fid)?.noTech) return false; // campaign: raiders and the like never study anything
  const f = g.factions[fid];
  const tech = techById(techId);
  if (!f || !tech || f.research || f.techs.includes(techId)) return false;
  if (tech.req && !f.techs.includes(tech.req)) return false;
  return (f.stock[GOLD] ?? 0) >= tech.gold; // research is paid in full up front, then takes `turns` to finish
}

/** Gold drained each turn while researching this tech (total cost spread over its duration). */
export const goldPerTurn = (tech: Tech): number => Math.ceil(tech.gold / tech.turns);

export function startResearch(g: GameState, fid: FactionId, techId: string): boolean {
  if (!canResearch(g, fid, techId)) return false;
  const f = g.factions[fid]!;
  const tech = techById(techId)!;
  f.stock[GOLD] = (f.stock[GOLD] ?? 0) - tech.gold; // charge the whole cost now, so the Gold visibly leaves the stockpile
  f.research = { tech: techId, turnsLeft: tech.turns, turnsTotal: tech.turns, goldPerTurn: goldPerTurn(tech) };
  return true;
}

export function cancelResearch(g: GameState, fid: FactionId): boolean {
  const f = g.factions[fid];
  if (!f || !f.research) return false;
  const tech = techById(f.research.tech);
  if (tech) f.stock[GOLD] = (f.stock[GOLD] ?? 0) + tech.gold; // paid up front, so a cancel refunds it in full (like production)
  f.research = null;
  return true;
}

export function techStatus(g: GameState, fid: FactionId, techId: string): 'done' | 'active' | 'available' | 'locked' {
  const f = g.factions[fid]!;
  if (f.techs.includes(techId)) return 'done';
  if (f.research?.tech === techId) return 'active';
  const tech = techById(techId)!;
  if (tech.req && !f.techs.includes(tech.req)) return 'locked';
  return 'available';
}

// --- diplomacy (Phase E): alliances (non-aggression) + trade agreements (shared income) ---
const pairKey = (a: FactionId, b: FactionId): [FactionId, FactionId] => (a < b ? [a, b] : [b, a]);
const inPair = (list: [FactionId, FactionId][], a: FactionId, b: FactionId): boolean =>
  list.some(([x, y]) => (x === a && y === b) || (x === b && y === a));
function addPair(list: [FactionId, FactionId][], a: FactionId, b: FactionId): void {
  if (!inPair(list, a, b)) list.push(pairKey(a, b));
}
function removePair(list: [FactionId, FactionId][], a: FactionId, b: FactionId): void {
  const [x, y] = pairKey(a, b);
  const i = list.findIndex(([p, q]) => p === x && q === y);
  if (i >= 0) list.splice(i, 1);
}

const stampKey = (a: FactionId, b: FactionId): string => (a < b ? `${a}-${b}` : `${b}-${a}`);

export const areAllied = (g: GameState, a: FactionId, b: FactionId): boolean => inPair(g.alliances, a, b);
export const atWar = (g: GameState, a: FactionId, b: FactionId): boolean => inPair(g.wars, a, b);

export type DiploResult = 'formed' | 'offered' | 'rejected';

// --- war & peace: declaring war and suing for peace each lock the other out for a few turns ---
/** Can `a` declare war on `b` right now? (Not already at war/allied, past the grace period, and not
 *  inside the cool-down that follows a recent peace.) */
export function canDeclareWar(g: GameState, a: FactionId, b: FactionId): boolean {
  if (a === b || atWar(g, a, b) || areAllied(g, a, b) || g.round <= NO_WAR_ROUNDS) return false;
  const brokeAt = g.allyStamp[stampKey(a, b)];
  if (brokeAt !== undefined && g.round - brokeAt < POST_ALLY_WAR_GRACE) return false; // just ended an alliance → a turn's grace before either side can strike
  const peacedAt = g.peaceStamp[stampKey(a, b)];
  return peacedAt === undefined || g.round - peacedAt >= DIPLO_COOLDOWN;
}
/** Can `a` and `b` make peace right now? (They are at war and have fought at least the minimum turns.) */
/** Some scenario factions never parley (raider bands): no peace, no alliance, no trade, either way. */
export const parleys = (g: GameState, a: FactionId, b: FactionId): boolean => !g.factions[a]?.noParley && !g.factions[b]?.noParley;

export function canMakePeace(g: GameState, a: FactionId, b: FactionId): boolean {
  if (!atWar(g, a, b) || !parleys(g, a, b)) return false;
  const warredAt = g.warStamp[stampKey(a, b)];
  return warredAt === undefined || g.round - warredAt >= DIPLO_COOLDOWN;
}
export function declareWar(g: GameState, from: FactionId, to: FactionId): boolean {
  if (!canDeclareWar(g, from, to)) return false;
  if (inPair(g.alliances, from, to)) g.allyStamp[stampKey(from, to)] = g.round; // betraying an ally starts the re-ally cool-down
  removePair(g.alliances, from, to); // war ends any alliance
  addPair(g.wars, from, to);
  g.warStamp[stampKey(from, to)] = g.round;
  return true;
}
export function makePeace(g: GameState, a: FactionId, b: FactionId): boolean {
  if (!parleys(g, a, b)) return false; // they do not treat with anyone
  if (atWar(g, a, b) && !canMakePeace(g, a, b)) return false; // locked into the war a while longer
  const wasWar = atWar(g, a, b);
  removePair(g.wars, a, b);
  if (wasWar) g.peaceStamp[stampKey(a, b)] = g.round;
  return true;
}
export function breakAlliance(g: GameState, a: FactionId, b: FactionId): void {
  if (inPair(g.alliances, a, b)) {
    g.allyStamp[stampKey(a, b)] = g.round; // re-allying is locked for a few turns after a break
    // the betrayed side hears about it: an inbox card for a human victim (`a` is always the breaker)
    if (g.factions[b]?.isPlayer && !g.factions[b].eliminated) g.messages.push({ kind: 'allyBroken', from: a, to: b });
  }
  removePair(g.alliances, a, b);
}
/** Can `a` and `b` form an alliance right now? (Not already allied/at war, and past the cool-down after any break.)
 *  LAND maps additionally cap every faction at ONE alliance: with everyone sharing one continent, unlimited
 *  pacts let the whole field ally into a permanent peace web (measured: all-AI mainland games sat fully
 *  allied to the round cap with zero wars). One pact each keeps the mainland fractious, someone is always
 *  outside the pact, so the war that ends the game can actually start. Isles keep the classic rule. */
export function canAlly(g: GameState, a: FactionId, b: FactionId): boolean {
  if (a === b || areAllied(g, a, b) || atWar(g, a, b) || !parleys(g, a, b)) return false;
  if (g.mapKind === 'land' && g.alliances.some(([x, y]) => x === a || y === a || x === b || y === b)) return false;
  const brokeAt = g.allyStamp[stampKey(a, b)];
  return brokeAt === undefined || g.round - brokeAt >= DIPLO_COOLDOWN;
}
// Turns remaining before each diplomatic action is allowed again between a and b (0 = allowed now). For UI countdowns.
export const peaceCooldownLeft = (g: GameState, a: FactionId, b: FactionId): number => {
  const t = g.warStamp[stampKey(a, b)];
  return t === undefined ? 0 : Math.max(0, DIPLO_COOLDOWN - (g.round - t));
};
export const warCooldownLeft = (g: GameState, a: FactionId, b: FactionId): number => {
  const t = g.peaceStamp[stampKey(a, b)];
  const peace = t === undefined ? 0 : Math.max(0, DIPLO_COOLDOWN - (g.round - t));
  const bt = g.allyStamp[stampKey(a, b)];
  const postBreak = bt === undefined ? 0 : Math.max(0, POST_ALLY_WAR_GRACE - (g.round - bt)); // one turn's grace after ending an alliance
  return Math.max(Math.max(0, NO_WAR_ROUNDS + 1 - g.round), peace, postBreak); // also blocked during the opening grace period
};
export const allyCooldownLeft = (g: GameState, a: FactionId, b: FactionId): number => {
  const t = g.allyStamp[stampKey(a, b)];
  return t === undefined ? 0 : Math.max(0, DIPLO_COOLDOWN - (g.round - t));
};

// --- alliance --- decision depends on the AI's personality, so proposals aren't auto-accepted
function aiAcceptsAlliance(g: GameState, from: FactionId, to: FactionId): boolean {
  const toFac = g.factions[to];
  if (!toFac) return false;
  const counts = regionCounts(g);
  const total = conquerableTotal(g);
  const cf = counts[from] ?? 0;
  const ct = counts[to] ?? 0;
  const leader = Math.max(...counts);
  if (cf === leader && cf > ct * 1.3 && ct > total * 0.15) return false; // never prop up a runaway leader
  const commonThreat = g.factions.some((x) => !x.eliminated && x.id !== from && x.id !== to && (counts[x.id] ?? 0) >= total * 0.45);
  if (commonThreat) return true; // band together only against a TRULY dominant third party (not a mid-lead)
  switch (toFac.strategy) {
    case 'aggressive':
      return cf <= ct * 0.7; // only leashes a much weaker suitor; otherwise it would rather fight
    case 'defensive':
      return cf <= ct * 1.8 + 1; // welcomes protection, even from a stronger friend
    case 'diplomat':
      return cf <= ct * 2.2 + 2; // allies with almost anyone who isn't dominating
    case 'opportunist':
    default:
      return cf <= ct * 1.2 + 1; // allies when it isn't a bad deal
  }
}
export function proposeAlliance(g: GameState, from: FactionId, to: FactionId): DiploResult {
  if (!canAlly(g, from, to)) return 'rejected'; // already allied/at war, or still inside the re-ally cool-down
  if (g.factions[to]!.isPlayer) {
    if (!g.offers.some((o) => o.from === from && o.to === to && o.kind === 'alliance')) g.offers.push({ id: g.nextOfferId++, from, to, kind: 'alliance', created: g.round });
    return 'offered';
  }
  if (aiAcceptsAlliance(g, from, to) && makePeace(g, from, to)) {
    // peace must clear first: if they're still inside the war cool-down, no alliance forms
    addPair(g.alliances, from, to);
    return 'formed';
  }
  return 'rejected';
}

/** Sue for peace. AI→AI ends the war outright; AI→player becomes a peace offer the player can accept. */
export function proposePeace(g: GameState, from: FactionId, to: FactionId): DiploResult {
  if (!atWar(g, from, to) || !canMakePeace(g, from, to)) return 'rejected';
  if (g.factions[to]!.isPlayer) {
    if (!g.offers.some((o) => o.from === from && o.to === to && o.kind === 'peace')) g.offers.push({ id: g.nextOfferId++, from, to, kind: 'peace', created: g.round });
    return 'offered';
  }
  return makePeace(g, from, to) ? 'formed' : 'rejected';
}

// --- trade: a single one-time swap. `from` hands over `give` and receives `want`, right now, once. ---
// `to` is the faction evaluating the offer: it gains `give` and parts with `want`.
function aiAcceptsTrade(g: GameState, to: FactionId, from: FactionId, give: ResAmt, want: ResAmt): boolean {
  const toFac = g.factions[to];
  if (!toFac || toFac.eliminated || atWar(g, from, to)) return false;
  const myRes: readonly number[] = g.islands[to]?.resources ?? [];
  const gainsWanted = !myRes.includes(give.res); // I gain a good I do not produce myself
  const givesSurplus = myRes.includes(want.res); // I give a good I actually produce (cheap for me)
  const affordable = (toFac.stock[want.res] ?? 0) >= want.amt;
  const fair = give.amt + 1 >= want.amt; // not badly lopsided against me
  const proposerDominant = (regionCounts(g)[from] ?? 0) >= conquerableTotal(g) * g.dominationPct * 0.85;
  return gainsWanted && givesSurplus && affordable && fair && !proposerDominant;
}

/** Execute a one-time swap immediately: `from` gives `give`, `to` gives `want`. Both must be able to pay
 *  and not be at war. Returns whether it went through. */
export function executeTrade(g: GameState, from: FactionId, to: FactionId, give: ResAmt, want: ResAmt): boolean {
  const a = g.factions[from];
  const b = g.factions[to];
  if (!a || !b || a.eliminated || b.eliminated || from === to || atWar(g, from, to) || !parleys(g, from, to)) return false;
  if ((a.stock[give.res] ?? 0) < give.amt || (b.stock[want.res] ?? 0) < want.amt) return false;
  a.stock[give.res]! -= give.amt;
  b.stock[give.res]! += give.amt;
  b.stock[want.res]! -= want.amt;
  a.stock[want.res]! += want.amt;
  return true;
}

/** Which living, not-at-war rivals would accept this one-time offer right now, for "propose to all". */
export function tradeTakers(g: GameState, from: FactionId, give: ResAmt, want: ResAmt): FactionId[] {
  return g.factions.filter((f) => f.id !== from && !f.eliminated && parleys(g, from, f.id) && aiAcceptsTrade(g, f.id, from, give, want)).map((f) => f.id);
}

export function proposeTrade(g: GameState, from: FactionId, to: FactionId, give: ResAmt, want: ResAmt): DiploResult {
  if (from === to || atWar(g, from, to) || !parleys(g, from, to)) return 'rejected';
  if (g.factions[to]!.isPlayer) {
    // Escrow the proposer's side the moment the offer is made, so the deal is ALWAYS honourable on their end:
    // the player can accept any time they can afford THEIR own side, no matter what the AI spends afterwards.
    // (This kills the "you have the goods but it says the rival can't honour it" stale-offer frustration.)
    if (!g.offers.some((o) => o.from === from && o.to === to && o.kind === 'trade')) {
      const a = g.factions[from]!;
      if ((a.stock[give.res] ?? 0) < give.amt) return 'rejected'; // can't even fund what it wants to offer
      a.stock[give.res]! -= give.amt;
      g.offers.push({ id: g.nextOfferId++, from, to, kind: 'trade', created: g.round, give, want });
    }
    return 'offered';
  }
  if (aiAcceptsTrade(g, to, from, give, want) && executeTrade(g, from, to, give, want)) return 'formed';
  return 'rejected';
}

// --- respond to an offer aimed at the player ---
/** Answer a pending offer. Returns whether it resolved: a rejected offer is always removed (true); an accepted
 *  offer is only removed and counted as done if it actually went through (e.g. the player can afford the trade,
 *  the alliance isn't war-locked), otherwise it stays so the player can act on it later, and this returns false. */
/** Hand a trade offer's escrowed goods back to the (still-living) proposer, e.g. when it is rejected or expires. */
export function refundOfferEscrow(g: GameState, offer: DiploOffer): void {
  if (offer.kind !== 'trade' || !offer.give) return;
  const from = g.factions[offer.from];
  if (from && !from.eliminated) from.stock[offer.give.res]! += offer.give.amt;
}

export function respondOffer(g: GameState, offerId: number, accept: boolean, responder?: FactionId): boolean {
  const i = g.offers.findIndex((o) => o.id === offerId);
  if (i < 0) return false;
  const offer = g.offers[i]!;
  const p = responder ?? g.playerFaction; // solo callers omit it; multiplayer passes the acting human
  if (offer.to !== undefined && offer.to !== p) return false; // only the offer's addressee may answer it
  if (!accept) {
    refundOfferEscrow(g, offer); // give the escrowed trade goods back to the proposer
    g.offers.splice(i, 1); // rejected / dismissed
    return true;
  }
  let ok = false;
  if (offer.kind === 'alliance') {
    if (canAlly(g, offer.from, p) && makePeace(g, offer.from, p)) {
      addPair(g.alliances, offer.from, p);
      ok = true;
    }
  } else if (offer.kind === 'peace') {
    ok = makePeace(g, offer.from, p);
  } else if (offer.kind === 'trade' && offer.give && offer.want) {
    // the proposer's `give` was escrowed when the offer was made; settle by taking the player's side and
    // releasing the escrow to them. Only the player's own side can be short now (and they control that).
    const me = g.factions[p];
    const from = g.factions[offer.from];
    if (me && from && !from.eliminated && !atWar(g, offer.from, p) && (me.stock[offer.want.res] ?? 0) >= offer.want.amt) {
      me.stock[offer.want.res]! -= offer.want.amt; // player pays their side
      from.stock[offer.want.res]! += offer.want.amt; // proposer receives it
      me.stock[offer.give.res]! += offer.give.amt; // player receives the escrowed goods (already off the proposer's books)
      ok = true;
    }
  }
  if (ok) g.offers.splice(i, 1); // only consume the offer when it truly resolved
  return ok;
}

/** Can this pending TRADE offer actually go through right now, and if not, whose fault is it? A stale offer can
 *  fail because the PROPOSER already spent the goods it promised (not the player's shortfall), the UI needs to
 *  tell those apart so it never blames the player for resources they clearly have. Non-trade offers are always ok. */
export interface TradeCheck {
  ok: boolean;
  playerShort: boolean; // the player can't pay their side (`want`)
  proposerShort: boolean; // the proposer can no longer pay its side (`give`)
  atWar: boolean; // the two are now at war, so no trade is possible
}
export function checkTradeOffer(g: GameState, offer: DiploOffer): TradeCheck {
  if (offer.kind !== 'trade' || !offer.give || !offer.want) return { ok: true, playerShort: false, proposerShort: false, atWar: false };
  const target = offer.to ?? g.playerFaction; // the human the offer is aimed at (solo: the player)
  const me = g.factions[target];
  const from = g.factions[offer.from];
  const playerShort = !me || (me.stock[offer.want.res] ?? 0) < offer.want.amt;
  const proposerShort = !from || from.eliminated; // the `give` is escrowed at offer time, so only a vanished proposer voids it
  const war = atWar(g, offer.from, target);
  return { ok: !playerShort && !proposerShort && !war, playerShort, proposerShort, atWar: war };
}

// --- AI (Phase F): each non-player faction plays a simple, competent turn ---
export const DOMINATION_PCT = 0.6; // control this share of all regions → instant win. Lowered from 0.7: on a 5-way
// map 70% (~4 islands) was almost unreachable so games stalemated to the round cap. 60% is decisive enough that
// a leading player can close out, without ending so early that a slightly-ahead AI snaps a win from under you.
// At this round the game ends; most regions wins (guarantees a finish). Raised 40 -> 60 with the move to
// total-conquest-only matches: at dominationPct 1 the cap was truncating exactly the long games (7/40 of
// 4p-large and 14/40 of 5p-large all-AI runs hit 40), and average playtime is a CrazyGames launch KPI.
export const ROUND_LIMIT = 60;
/** A mainland board is ~2.4x the tiles, so a human conquering it tile by tile needs more than the isles'
 *  60 rounds. Kept modest on purpose: measured, a LONGER clock buys the AI no extra wins at all, it only
 *  drags a stalemate out (scripts/campaignbalance.ts at 60 vs 85 rounds), so the clock is a budget for a
 *  human finishing a war, not a difficulty lever. The difficulty lever is the handicap set. */
export const LAND_ROUND_LIMIT = 72;
export const roundLimitFor = (mapKind: MapKind): number => (mapKind === 'land' ? LAND_ROUND_LIMIT : ROUND_LIMIT);

export function runAiTurn(g: GameState, fid: FactionId): void {
  const f = g.factions[fid]!;
  if (f.eliminated) return;
  // (voyage resolution is NOT here: it lives at the walk seams (runAiSegment / the human-break sites /
  // runFullAiRound) so a seat's boats land at the same walk position on every client even when the seat
  // flips between human and AI control mid-match (aiTakeover / reclaimSeat).)
  const K = DIFF[f.aiDifficulty ?? g.difficulty]; // difficulty knobs, per-seat in multiplayer (host fills empty slots)

  // stalled boats: a crossing whose beach was taken must be re-aimed, or the unit sits at sea for the
  // rest of the match AND keeps eating an army-supply slot. Prefer a hostile shore, then free ground,
  // then home: a landing beats circling. (keepReplay: the human watches this in the AI-phase replay.)
  for (let vi = g.voyages.length - 1; vi >= 0; vi--) {
    const v = g.voyages[vi];
    if (!v || v.owner !== fid || !v.stalled) continue;
    const opts = voyageTargets(g, vi);
    if (!opts.length) continue;
    const score = (id: RegionId): number => {
      const r = g.regions[id]!;
      if (r.unit) return 3; // an assault: worth pressing if the odds are not hopeless
      if (r.owner >= 0 && r.owner !== fid) return 5; // an empty enemy shore: take it
      if (r.owner === NEUTRAL) return 4; // free land
      return 1; // our own coast: at least the troops come home
    };
    const best = [...opts].sort((a, b) => score(b) - score(a) || a - b)[0]!;
    rerouteVoyage(g, fid, vi, best, true);
  }

  // diplomacy: now and then offer a fair one-time swap for a good this faction lacks (give surplus, want a shortage)
  if ((g.round + fid) % 3 === 0) {
    const myRes: readonly number[] = g.islands[fid]?.resources ?? [];
    for (const other of g.factions) {
      if (other.id === fid || other.eliminated || atWar(g, fid, other.id) || !parleys(g, fid, other.id)) continue;
      const theirRes: readonly number[] = g.islands[other.id]?.resources ?? [];
      const want = theirRes.find((r) => !myRes.includes(r)); // a good I lack that they produce
      const give = myRes.find((r) => !theirRes.includes(r)); // a good I produce that they lack
      if (want != null && give != null && (f.stock[give] ?? 0) >= 2) {
        proposeTrade(g, fid, other.id, { res: give, amt: 2 }, { res: want, amt: 2 });
        break;
      }
    }
  }

  // diplomacy 2: de-escalate a war we're clearly losing, and now and then seek an alliance. proposePeace
  // ends an AI↔AI war outright or offers the player peace; proposeAlliance routes AI targets through
  // aiAcceptsAlliance (which bands rivals together against a runaway leader) and player targets into the
  // offer inbox. All deterministic (keyed on round/fid/region counts) so replays stay reproducible.
  {
    const dc = regionCounts(g);
    const dmine = dc[fid] ?? 0;
    for (const other of g.factions) {
      if (other.id === fid || other.eliminated || !atWar(g, fid, other.id) || !canMakePeace(g, fid, other.id)) continue;
      if ((dc[other.id] ?? 0) > dmine * 1.25) {
        // rate-limit repeat asks at a HUMAN: a losing AI re-toasting "peace?" every single round reads
        // as spam. One ask per pair per DIPLO_COOLDOWN rounds; AI-to-AI peace stays immediate.
        if (other.isPlayer) {
          const key = fid < other.id ? `${fid}-${other.id}` : `${other.id}-${fid}`;
          const stamps = (g.peaceAskStamp ??= {});
          if ((stamps[key] ?? -99) + DIPLO_COOLDOWN > g.round) continue;
          stamps[key] = g.round;
        }
        proposePeace(g, fid, other.id); // sue for peace when clearly outmatched
      }
    }
    if ((g.round + fid) % 6 === 0 && !g.factions.some((x) => atWar(g, fid, x.id)) && g.factions.filter((x) => !x.eliminated && x.id !== fid).length > K.closerMaxFactions) {
      // ally less often (every ~6 rounds, not 4) so the AI world stays fractious and fights ITSELF, otherwise
      // a lone human is the only unallied faction and everyone is free to pile on them. Skip entirely once the
      // field is down to a near-1v1 (<= closerMaxFactions rivals): a dominant AI must NOT pact the last enemy.
      const partner = g.factions
        .filter((o) => o.id !== fid && !o.eliminated && canAlly(g, fid, o.id))
        .sort((a, b) => (dc[b.id] ?? 0) - (dc[a.id] ?? 0))[0];
      if (partner) proposeAlliance(g, fid, partner.id);
    }
  }

  // war: only declare on a bordering rival we are clearly stronger than, never pile onto someone already
  // fighting two others (no 4-on-1 dogpiles), and only now and then, so wars are winnable, not curbstomps.
  if (g.round > NO_WAR_ROUNDS && !g.factions.some((x) => atWar(g, fid, x.id))) {
    const counts = regionCounts(g);
    const border = new Map<FactionId, number>();
    for (const reg of g.regions) {
      if (reg.owner !== fid || !reg.unit) continue;
      for (const n of reg.neighbors) {
        const o = g.regions[n]!.owner;
        if (o >= 0 && o !== fid && !areAllied(g, fid, o)) border.set(o, (border.get(o) ?? 0) + 1);
      }
      // a sea rival only counts as reachable if we can actually launch at it (a Harbor here) AND its
      // island still offers a beach to land on (neutral, or the rival's own coast). A war that cannot
      // put boots on sand is noise, and its declaration used to lock the anti-dogpile slots for rivals
      // who COULD fight.
      if (islandHasHarbor(g, fid, reg.island)) {
        const links = g.islands[reg.island]?.seaLinks ?? [];
        for (const orr of g.regions) {
          if (orr.owner < 0 || orr.owner === fid || !links.includes(orr.island) || areAllied(g, fid, orr.owner)) continue;
          const landable = g.islands[orr.island]?.regions.some((id) => {
            const beach = g.regions[id]!;
            return isCoastal(g, beach) && !impassable(beach.terrain) && (beach.owner === NEUTRAL || beach.owner === orr.owner);
          });
          if (landable) border.set(orr.owner, (border.get(orr.owner) ?? 0) + 0.5);
        }
      }
    }
    const warsOn = (t: FactionId): number => g.wars.filter(([a, b]) => a === t || b === t).length;
    const total = conquerableTotal(g);
    const mine = counts[fid] ?? 0;
    const bordered = [...border.keys()];
    // personality: how much of a strength edge a faction needs before it starts a war, and how eagerly. On Hard,
    // biasShift(+1) makes every personality one step more warlike, and warGateAlways drops the throttle entirely.
    const bias = { aggressive: 3, opportunist: 1, defensive: -2, diplomat: -3 }[f.strategy] + K.biasShift;
    const warGate =
      K.warGateAlways ||
      f.strategy === 'aggressive' ||
      f.strategy === 'opportunist' ||
      (f.strategy === 'defensive' && (g.round + fid) % 2 === 0) ||
      (f.strategy === 'diplomat' && (g.round + fid) % 3 === 0);
    // Anti-snowball: a runaway leader (big slice of the map, clearly ahead of me) is fair game to gang up on
    // even if someone already is, but capped at TWO attackers, a coalition rather than an inescapable 4-on-1
    // (so a leading player is challenged, not curb-stomped every turn the moment a peace cool-down lapses).
    const isLeader = (t: FactionId): boolean => (counts[t] ?? 0) > total * 0.28 && (counts[t] ?? 0) >= mine * 1.4;
    // SOLO: the human gets a personal early grace + a harder-to-clear bar, so a lone player isn't ground
    // down by the AIs. MULTIPLAYER plays symmetric: no faction gets the grace or the extra margin.
    const warblePlayer = (t: FactionId): boolean => g.mode === 'mp' || t !== g.playerFaction || g.round >= K.warGrace;
    const humanMargin = (t: FactionId): number => (g.mode !== 'mp' && t === g.playerFaction ? K.warMargin : 0);
    const leaderTarget = bordered.filter((t) => isLeader(t) && warsOn(t) < 2 && warblePlayer(t)).sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0))[0];
    // EASY PREY comes first: a bordered rival at barely half my size is the natural 4X target, and the
    // frontier sort below never picked it (on isles a human's sea border scores 0.5 a tile, so the AI
    // forever preferred its meatier AI neighbours; measured: a PASSIVE 1-tile player drew ZERO attacks
    // in 40 rounds). The human grace/margin still applies, so this punishes stagnation, not existence.
    const landBordered = new Set<FactionId>();
    for (const reg of g.regions) {
      if (reg.owner !== fid) continue;
      for (const n of reg.neighbors) {
        const o = g.regions[n]!.owner;
        if (o >= 0 && o !== fid) landBordered.add(o);
      }
    }
    const preyTarget = bordered
      .filter((t) => (warsOn(t) === 0 || (landBordered.has(t) && warsOn(t) < 2)) && warblePlayer(t) && (counts[t] ?? 0) * 1.6 <= mine && mine + bias >= (counts[t] ?? 0) + humanMargin(t))
      .sort((a, b) => (counts[a] ?? 0) - (counts[b] ?? 0))[0];
    // Otherwise attack the rival we share the most frontier with (opportunists lean toward the weakest),
    // provided we clear our personality's strength bar and no one else is already piling on them.
    const rivalTarget = preyTarget ?? bordered
      .filter((t) => warsOn(t) === 0 && warblePlayer(t) && mine + bias >= (counts[t] ?? 0) + humanMargin(t))
      .sort((a, b) => (border.get(b) ?? 0) - (border.get(a) ?? 0) || (counts[a] ?? 0) - (counts[b] ?? 0))[0];
    // ENDGAME CLOSER (both difficulties): once the field has collapsed to a near-1v1 and I clearly lead, commit to
    // finishing the last rival even if my personality is timid and it isn't `bordered` yet. `reachable` relaxes the
    // reach test: a rival counts if I own ANY coastal tile on an island sea-linked to it (no built Harbor / garrison
    // required) - declaring war then drives the harbor pass + movement loop to actually prosecute it. It bypasses
    // warGate, the personality bias and the war margin, but still honours the human grace + all war cool-downs.
    // if the field is down to a near-1v1 and I lead, but my only surviving rivals are ALLIES, break the pact so I
    // can finish them - a farmed truce must never stall a won game. The closer then declares once the 1-turn grace
    // lapses (next turn). Guarded on dominance so an underdog doesn't suicidally void its protective alliance.
    const livingRivals = g.factions.filter((x) => !x.eliminated && x.id !== fid);
    if (livingRivals.length > 0 && livingRivals.length <= K.closerMaxFactions && mine >= Math.max(0, ...livingRivals.map((x) => counts[x.id] ?? 0)))
      for (const r of livingRivals) if (areAllied(g, fid, r.id)) breakAlliance(g, fid, r.id);
    const reachIslands = new Set<number>();
    const reachable = new Set<FactionId>();
    for (const reg of g.regions) {
      if (reg.owner !== fid) continue;
      for (const n of reg.neighbors) { const o = g.regions[n]!.owner; if (o >= 0 && o !== fid && !areAllied(g, fid, o)) reachable.add(o); } // a land border to a rival (no garrison needed just to INTEND war; the movement loop garrisons it)
      if (isCoastal(g, reg)) for (const lk of g.islands[reg.island]?.seaLinks ?? []) reachIslands.add(lk); // a coast we could sail from
    }
    for (const reg of g.regions) if (reachIslands.has(reg.island) && reg.owner >= 0 && reg.owner !== fid && !areAllied(g, fid, reg.owner)) reachable.add(reg.owner); // a rival across a sea link
    const aliveRivals = g.factions.filter((x) => !x.eliminated && x.id !== fid && !areAllied(g, fid, x.id));
    const rivalMax = Math.max(0, ...aliveRivals.map((x) => counts[x.id] ?? 0));
    // fire once it's a near-1v1 and I'm at least tied for the lead. The total*0.3 floor only guards Hard's 2-rival
    // case from firing turn-1 (everyone tiny); a true 1v1 (one rival left) is always the endgame, so skip the floor.
    const closing = aliveRivals.length <= K.closerMaxFactions && mine >= rivalMax * K.closerLead && (aliveRivals.length <= 1 || mine >= total * 0.3);
    const closerTarget = closing
      ? [...reachable].filter((t) => warblePlayer(t) && canDeclareWar(g, fid, t)).sort((a, b) => (counts[a] ?? 0) - (counts[b] ?? 0))[0]
      : undefined;
    const target = leaderTarget ?? closerTarget ?? (warGate ? rivalTarget : undefined);
    if (target != null) declareWar(g, fid, target);
  }

  // --- threat model: which of my regions can an enemy take next turn, and with what unit types? ---
  // Reachability ignores the `moved` flag on purpose: a threat is about NEXT turn, when it resets.
  const canReach = (from: Region, to: Region): boolean =>
    // a mainland is ONE landmass: reach is pure geometry there. Only across water does the island
    // graph decide, and then only from a port (seaLinks is empty on land, so the old test blinded
    // the AI to every enemy standing in the next province, which is where its wars actually are).
    g.mapKind === 'land' || from.island === to.island
      ? // adjacent (any unit) OR two tiles off for a ranged Archer, which strikes at range without closing
        from.neighbors.includes(to.id) || (!!from.unit && hexDistance(from, to) <= unitRange(from.unit.type))
      : (g.islands[from.island]?.seaLinks.includes(to.island) ?? false) && from.owner >= 0 && islandHasHarbor(g, from.owner, from.island);
  const typeTally = [0, 0, 0, 0];
  const threatenedRegion = new Set<RegionId>();
  if (g.round >= NO_WAR_ROUNDS) {
    for (const er of g.regions) {
      if (er.owner === NEUTRAL || er.owner === fid || !er.unit || !atWar(g, fid, er.owner)) continue;
      for (const mr of g.regions) {
        if (mr.owner !== fid || !canReach(er, mr)) continue;
        const isSea = er.island !== mr.island;
        // a threat = the enemy can capture (empty) or kill my defender in one hit
        const willTake = !mr.unit || attackDamage(er.unit, g.factions[er.owner]!, mr.unit, mr, g.factions[mr.owner]!, isSea, regionCountOf(g, mr.owner), g.difficulty, shielded(g, g.factions[mr.owner]!)) >= mr.unit.hp;
        if (willTake) {
          typeTally[er.unit.type] = (typeTally[er.unit.type] ?? 0) + 1;
          threatenedRegion.add(mr.id);
        }
      }
    }
  }
  const underThreat = threatenedRegion.size > 0;
  // counter the most common attacker type: train the unit that beats it (COUNTER[enemyType], which does)
  let enemyType = 0;
  for (let t = 1; t < 4; t++) if ((typeTally[t] ?? 0) > (typeTally[enemyType] ?? 0)) enemyType = t;
  const counterType = COUNTER[enemyType]!;

  // is there a rival across the water worth boarding a boat for? (drives the ferry-boarding move below)
  // an island is a war target if ANY of its tiles belongs to a faction we fight: the beaches we land
  // on are usually neutral, and the old owner-of-the-beach test left an enemy tucked inland invisible
  // (measured: a warring AI with a harbor never boarded a single boat against a 1-tile island player)
  const warIsland = (island: number): boolean => g.regions.some((r) => r.island === island && r.owner >= 0 && atWar(g, fid, r.owner));
  const seaRivalNear = g.regions.some(
    (p) => p.owner === fid && p.buildings.includes(BUILDING.Harbor) && seaReach(g, p).some((r) => warIsland(r.island)),
  );

  // research: prioritise unlocking a new unit type (Archer, then Rider) so the AI fields a full roster;
  // otherwise bias to the military branch when at war, else take the cheapest available tech.
  if (!f.research) {
    const avail = TECHS.filter((t) => techStatus(g, fid, t.id) === 'available' && canResearch(g, fid, t.id)).sort((a, b) => a.gold - b.gold);
    const pick = avail.find((t) => t.unlocks !== undefined) ?? (underThreat ? avail.find((t) => t.branch === 'mil') : undefined) ?? avail[0];
    if (pick) startResearch(g, fid, pick.id);
  }

  // movement: each unit picks its best action: kill+capture > capture > safe ranged chip > favourable
  // fight, and never throws itself away for nothing. The capital garrison joins in too (everyone starts
  // as a lone capital, so it MUST be able to spill out to expand), but only onto safe neutral land and
  // never while the home is threatened, so it doesn't charge off to war and leave the capital open.
  for (const reg of g.regions) {
    // skip units that have already used a move this turn (movesLeft below their max). One action per unit per AI
    // pass, so a Rider spends one of its two points here; the loop won't re-act it as it advances.
    if (reg.owner !== fid || !reg.unit || reg.unit.movesLeft < unitMoves(reg.unit.type)) continue;
    const u = reg.unit;
    // the campaign objective is the level's prize: its holder defends it exactly like a capital
    const isCap = reg.isCapital || reg.id === g.objective?.region;
    const t = unitTargets(g, reg.id);
    const opts: { to: RegionId; score: number }[] = [];
    const rateAttack = (toId: RegionId, sea: boolean): void => {
      const to = g.regions[toId]!;
      if (!to.unit) return;
      const defFac = g.factions[to.owner]!;
      const dist = sea ? 1 : hexDistance(reg, to);
      const ranged = !sea && dist === 2;
      const dealt = attackDamage(u, f, to.unit, to, defFac, sea, regionCountOf(g, to.owner), g.difficulty, shielded(g, defFac), ranged);
      const kills = to.unit.hp - dealt <= 0;
      // a melee/sea defender now strikes back even as it dies (the last-stand counter), so a close kill is no
      // longer free; only a ranged kill draws no counter. Model the blow the attacker would take back.
      const counters = dist <= unitRange(to.unit.type) && !(kills && ranged);
      const back = counters ? Math.max(1, attackDamage(to.unit, defFac, u, reg, f, false, regionCountOf(g, fid), g.difficulty, shielded(g, f), ranged) - RETALIATION_PENALTY) : 0;
      const attackerDies = back >= u.hp; // the counter (or dying last-stand blow) cuts the attacker down too
      if (!kills && attackerDies) return; // suicidal chip with no kill → skip
      let score = 2 + dealt - back * 1.5;
      if (kills) {
        score += 8;
        const captures = !ranged && !attackerDies; // a mutual-death assault clears the tile but can't take it
        if (captures) score += 6 + (to.isCapital ? 20 : 4); // melee/sea kill also captures the tile
        else if (to.isCapital) score += 6; // ranged kill, or trading the unit to clear a capital defender
      }
      opts.push({ to: toId, score });
    };
    if (!isCap) {
      for (const n of t.melee) rateAttack(n, false);
      for (const n of t.ranged) rateAttack(n, false);
      for (const n of t.sea) if (g.regions[n]!.unit) rateAttack(n, true);
    }
    for (const n of t.move) {
      const to = g.regions[n]!;
      if (to.owner === fid) {
        // a crossing now launches only from a PORT tile, so walking onto an empty own harbor is how an
        // army boards: worth doing when there is a rival across the water to reach
        if (!f.garrison && !isCap && !to.unit && to.buildings.includes(BUILDING.Harbor) && !reg.buildings.includes(BUILDING.Harbor) && seaRivalNear) opts.push({ to: n, score: 5 });
        continue;
      }
      if (f.garrison) continue; // a garrison holds its walls: it never expands onto land it does not own
      const neutral = to.owner === NEUTRAL;
      if (isCap && (!neutral || underThreat)) continue; // the capital only spreads onto empty neutral land, and never when home is at risk
      opts.push({ to: n, score: neutral ? 3 : 7 }); // capture enemy > claim neutral
    }
    // amphibious capture of an empty enemy/neutral beachhead. Own empty tiles are also sea-reachable now (the
    // player can ferry troops home), but for the AI that's a low-value shuffle, so skip it and keep its aggression.
    if (!isCap) for (const n of t.sea) if (!g.regions[n]!.unit && g.regions[n]!.owner !== fid) opts.push({ to: n, score: 7 });
    // LAND maps, at war, nothing worthwhile in reach: march the reserve TOWARD the nearest enemy instead
    // of idling. Interior units otherwise sit garrisoned forever (no sea assault exists to flank with),
    // and measured mainland wars ground to attrition stalemates at the round cap. A step must strictly
    // close the distance; ties break on region id so the column is deterministic. Capitals never march.
    if (g.mapKind === 'land' && !f.garrison && !isCap && (!opts.length || opts[0]!.score <= 0) && g.factions.some((x) => atWar(g, fid, x.id))) {
      let targetDist = Infinity;
      let enemyReg: Region | null = null;
      for (const er of g.regions) {
        if (er.owner < 0 || er.owner === fid || !atWar(g, fid, er.owner)) continue;
        const d = hexDistance(reg, er);
        if (d < targetDist || (d === targetDist && enemyReg && er.id < enemyReg.id)) {
          targetDist = d;
          enemyReg = er;
        }
      }
      if (enemyReg) {
        const step = t.move
          .map((n) => g.regions[n]!)
          .filter((to) => to.owner === fid && !to.isCapital) // reposition over OWN ground only; frontier tiles are the loop's business
          .map((to) => ({ to, d: hexDistance(to, enemyReg!) }))
          .filter((s) => s.d < targetDist)
          .sort((a, b) => a.d - b.d || a.to.id - b.to.id)[0];
        if (step) opts.push({ to: step.to.id, score: 1 });
      }
    }
    if (!opts.length) continue;
    opts.sort((a, b) => b.score - a.score);
    if (opts[0]!.score <= 0) continue;
    const dest = opts[0]!.to;
    const ut = u.type;
    const res = moveOrAttack(g, reg.id, dest, fid);
    if (res) {
      g.replay.push({ kind: 'unit', faction: fid, unitType: ut, from: reg.id, to: dest, combat: res.kind, captured: res.captured, defenderDied: res.defenderDied, attackerDied: res.attackerDied, attackerHp: res.attackerHp, defenderHp: res.defenderHp, retaliated: res.retaliated });
    }
  }

  // Volley pass: an arrow costs no movement, so every bowman that still has its shot looses one after the
  // march (the loop above skips units that already acted, which used to leave archers that had merely
  // repositioned standing there with a full quiver). Kills first, then the heaviest wound, and never a duel
  // it loses: a fellow archer shoots back.
  for (const reg of g.regions) {
    const u = reg.unit;
    if (!u || u.owner !== fid || u.shot || unitRange(u.type) < 2) continue;
    let best: { to: RegionId; score: number } | null = null;
    for (const n of unitTargets(g, reg.id).ranged) {
      const p = previewCombat(g, reg.id, n, fid);
      if (!p || p.attackerDies) continue; // trading a bowman for a bowman is not worth the arrow
      const score = p.dealt + (p.defenderDies ? 8 : 0) + (g.regions[n]!.isCapital ? 3 : 0) - (p.retaliation ?? 0);
      if (!best || score > best.score || (score === best.score && n < best.to)) best = { to: n, score };
    }
    if (!best) continue;
    const ut = u.type;
    const res = moveOrAttack(g, reg.id, best.to, fid);
    if (res) g.replay.push({ kind: 'unit', faction: fid, unitType: ut, from: reg.id, to: best.to, combat: res.kind, captured: res.captured, defenderDied: res.defenderDied, attackerDied: res.attackerDied, attackerHp: res.attackerHp, defenderHp: res.defenderHp, retaliated: res.retaliated });
  }

  // Neutral land is now taken by moving units onto it (handled in the movement loop above), so there
  // is no separate "claim" step. Instead the AI invests in Harbors to open up overseas conquest.

  // Harbor pass (priority): every rival lives across the sea, so without a Harbor a faction can never
  // attack anyone, AND each Harbor raises the army supply cap by 1. So a faction builds a port both to
  // reach a sea rival and, once its army is maxed out, purely to be able to field more troops.
  const myIslands = new Set<number>();
  for (const reg of g.regions) if (reg.owner === fid) myIslands.add(reg.island);
  const armyMaxed = unitCount(g, fid) >= unitCap(g, fid); // out of supply → more Harbors = a bigger army
  const wantsHarborOn = (island: number): boolean => {
    const hasPort = islandHarborPlanned(g, fid, island);
    const links = g.islands[island]?.seaLinks ?? [];
    const seaRival = links.some((lk) => g.factions.some((x) => !x.eliminated && x.id !== fid && g.regions.some((r) => r.island === lk && r.owner === x.id)));
    return !hasPort && seaRival; // a port to reach a rival across the sea (its gold is a bonus, not a driver)
  };
  for (const island of myIslands) {
    if (!wantsHarborOn(island)) continue;
    const spot = g.regions
      .filter((r) => r.owner === fid && r.island === island && !r.production && canBuildHarbor(g, r.id, fid))
      // prefer a safe, already-garrisoned interior tile so the port is defended and no train slot is lost
      .sort((a, b) => Number(threatenedRegion.has(a.id)) - Number(threatenedRegion.has(b.id)) || Number(!!b.unit) - Number(!!a.unit))[0];
    if (spot) startHarbor(g, spot.id, fid);
  }
  // Barracks pass: the muster ground, on any map. Troops can ONLY be raised on one (or on the capital),
  // so a faction wants a couple of them early, and more once its army is capped out.
  const musterCount = g.regions.filter((r) => r.owner === fid && r.buildings.includes(BUILDING.Barracks)).length;
  if (armyMaxed || musterCount < 2) {
    const spot = g.regions
      .filter((r) => r.owner === fid && !r.production && canBuildBarracks(g, r.id, fid))
      .sort((a, b) => Number(threatenedRegion.has(a.id)) - Number(threatenedRegion.has(b.id)) || Number(!!b.unit) - Number(!!a.unit))[0];
    if (spot) startBarracks(g, spot.id, fid);
  }

  // production: garrison territory so nothing is a free capture. Under threat, pour out counter-units;
  // otherwise field a standing army (frontline first), sparing only interior regions for Markets.
  const trainCap = underThreat ? 99 : 4;
  let trained = 0;
  // frontline regions (those an enemy threatens, plus capitals) get units first
  const hold = (r: Region): boolean => threatenedRegion.has(r.id) || r.isCapital || r.id === g.objective?.region;
  const order = [...g.regions].sort((a, b) => Number(hold(b)) - Number(hold(a)));
  for (const reg of order) {
    if (reg.owner !== fid || reg.production) continue;
    let didTrain = false;
    // a RESERVE behind a threatened muster ground: the recruit waits in the yard and steps up the round after
    // the defender falls, so a frontline barracks is never left bare by one lost fight
    const wantReserve = underThreat && hold(reg) && canMuster(reg) && reg.reserve == null;
    if ((!reg.unit || wantReserve) && trained < trainCap) {
      // want the counter (or a rotating pick), but Archer/Rider may be locked/unaffordable, fall back
      // through the always-available base units so a train slot is never wasted on an impossible unit.
      const prefer = underThreat ? counterType : (((fid + g.round + reg.id) % 4) as UnitType);
      const type = ([prefer, 0, 3, 1, 2] as UnitType[]).find((t) => canTrain(g, reg.id, fid, t));
      if (type !== undefined) {
        startTrain(g, reg.id, fid, type);
        trained += 1;
        didTrain = true;
      }
    }
    if (!didTrain && !underThreat && !reg.buildings.includes(BUILDING.Market) && canBuildMarket(g, reg.id, fid)) {
      startMarket(g, reg.id, fid);
    }
  }
}

// Deterministic leader pick: most regions among the living; a tie goes to the classic player faction if
// it is among the tied (preserves the solo "player wins ties" rule), else to the lowest faction id.
// Shared by checkGameEnd's terminal paths and aiTakeover's last-human finish, so both crown identically.
function leaderOf(g: GameState, counts: number[]): FactionId | null {
  const alive = g.factions.filter((f) => !f.eliminated && (counts[f.id] ?? 0) > 0);
  if (!alive.length) return null;
  const max = Math.max(...alive.map((f) => counts[f.id] ?? 0));
  const tied = alive.filter((f) => (counts[f.id] ?? 0) === max).map((f) => f.id);
  return tied.includes(g.playerFaction) ? g.playerFaction : (tied[0] ?? null);
}
function finishMatch(g: GameState, winner: FactionId | null): void {
  g.phase = 'over';
  g.winner = winner;
  g.result = winner === g.playerFaction ? 'win' : 'lose'; // solo perspective; MP clients read `winner` vs their own faction
}

export function checkGameEnd(g: GameState): void {
  if (g.phase !== 'playing') return;
  const counts = regionCounts(g);
  const p = g.playerFaction;
  const total = conquerableTotal(g);
  const alive = g.factions.filter((f) => !f.eliminated && (counts[f.id] ?? 0) > 0);
  // SOLO: the game ends the moment THE human falls (rivals may fight on; the player's game is over)
  if (g.mode !== 'mp' && (counts[p] ?? 0) === 0) return finishMatch(g, null);
  // MP: when every human seat has fallen (in a game that had any), the leading survivor takes it
  if (g.mode === 'mp' && g.factions.some((f) => f.isPlayer) && !alive.some((f) => f.isPlayer)) return finishMatch(g, leaderOf(g, counts));
  // a campaign objective: taking THE place the level is about wins it outright, however much of the
  // map is still in rival hands (the mission was the keep, not the census)
  if (g.objective && g.regions[g.objective.region]?.owner === p) return finishMatch(g, p);
  // domination: controlling the winning share of the map is decisive for ANY faction
  const dominator = alive.find((f) => (counts[f.id] ?? 0) / total >= g.dominationPct);
  if (dominator) return finishMatch(g, dominator.id);
  // last realm standing
  if (alive.length <= 1) return finishMatch(g, alive[0]?.id ?? null);
  // time limit: the largest realm wins (guarantees a finish). Strictly past the limit so that every
  // seat, human and AI alike, gets to play the final round before the leader is crowned.
  if (g.round > g.roundLimit) return finishMatch(g, leaderOf(g, counts));
}

/** Advance the whole turn as a GENERATOR: it yields the id of each new offer an AI makes to the player,
 *  pausing the AI sequence so the caller can prompt for a response before the remaining AIs act (their
 *  decisions then account for the player's answer). Drain it (see endTurn) for a non-interactive turn.
 *  Grants income, advances research + builds, runs the AI in a rotating order, then ends the round. */
/** The fixed per-game play order (falls back to natural id order for saves that predate turnOrder). */
export const turnOrderOf = (g: GameState): FactionId[] => (g.turnOrder && g.turnOrder.length === g.factions.length ? g.turnOrder : g.factions.map((f) => f.id));

/** Round tick at the turn-order boundary: grant income, advance research, rest & regen units (and reset their
 *  moved flag), then complete any finished production. Production completions are recorded on g.replay so they
 *  animate in the AI-phase replay. Runs ONCE per round (not per faction). */
export function roundUpkeep(g: GameState): void { // exported for the test harnesses: one round boundary, no turn walk
  for (const f of g.factions) {
    if (f.eliminated) continue;
    const inc = factionIncome(g, f.id);
    // income tops out at STOCK_CAP; a stock already over it (e.g. from plunder) is kept, not drained
    for (let r = 0; r < RESOURCES; r++) f.stock[r]! = Math.max(f.stock[r]!, Math.min(STOCK_CAP, f.stock[r]! + inc[r]!));
    if (f.research) {
      // the Gold was already paid in full at startResearch; the tech just needs its turns to finish
      f.research.turnsLeft -= 1;
      if (f.research.turnsLeft <= 0) {
        f.techs.push(f.research.tech);
        f.research = null;
      }
    }
  }
  // rest & recover: a unit that held its ground on friendly land (spent no move) regains a pip; then refill move points
  for (const reg of g.regions) {
    if (!reg.unit) continue;
    // a unit heals only if it truly RESTED: it neither marched nor loosed an arrow (an archer that shot from
    // cover was in the fight, so it does not also mend that round)
    if (reg.unit.movesLeft >= unitMoves(reg.unit.type) && !reg.unit.shot && reg.unit.owner === reg.owner) reg.unit.hp = Math.min(unitMaxHp(reg.unit.type), reg.unit.hp + REGEN_HP);
    reg.unit.movesLeft = unitMoves(reg.unit.type);
    reg.unit.shot = false; // fresh quiver: one arrow again this round
  }
  // reserves muster out FIRST, and only at a round boundary: a soldier waiting in the yard never pops into
  // being the instant the tile empties mid-turn, it forms up and marches out on the next round. (Running this
  // before the production tick also means a reserve created below waits a full round, as intended.)
  for (const reg of g.regions) {
    if (reg.reserve == null) continue;
    if (reg.owner === NEUTRAL || reg.unit) continue; // still occupied (or the ground was lost): keep waiting
    const type = reg.reserve;
    reg.reserve = null;
    reg.unit = { type, owner: reg.owner, movesLeft: unitMoves(type), hp: unitMaxHp(type) };
    bumpBuilt(g, reg.owner, type);
    g.replay.push({ kind: 'train', faction: reg.owner, region: reg.id, unitType: type });
  }
  for (const reg of g.regions) {
    if (!reg.production) continue;
    reg.production.turnsLeft -= 1;
    if (reg.production.turnsLeft <= 0) {
      const prod = reg.production;
      if (prod.kind === 'building') {
        reg.buildings.push(prod.building);
        g.replay.push({ kind: 'build', faction: reg.owner, region: reg.id, building: prod.building });
      } else if (!reg.unit) {
        reg.unit = { type: prod.unitType, owner: reg.owner, movesLeft: unitMoves(prod.unitType), hp: unitMaxHp(prod.unitType) };
        bumpBuilt(g, reg.owner, prod.unitType);
        g.replay.push({ kind: 'train', faction: reg.owner, region: reg.id, unitType: prod.unitType });
      } else if (reg.unit.owner === reg.owner) {
        // own tile is occupied (a unit is standing on the muster ground), so the recruit falls in as a RESERVE
        // and waits in the yard: it marches out at the round boundary AFTER the tile is clear, never the moment
        // the standing unit steps away. (A reserve slot is always free here: canTrain refuses a new order while
        // one waits, so the only way to reach this line with a full yard is a save from before reserves existed,
        // where the old neighbour-deploy / refund fallback still applies.)
        if (reg.reserve == null) reg.reserve = prod.unitType;
        else {
          const spot = reg.neighbors.map((n) => g.regions[n]!).find((nr) => nr.owner === reg.owner && !nr.unit);
          if (spot) {
            spot.unit = { type: prod.unitType, owner: reg.owner, movesLeft: unitMoves(prod.unitType), hp: unitMaxHp(prod.unitType) };
            bumpBuilt(g, reg.owner, prod.unitType);
            g.replay.push({ kind: 'train', faction: reg.owner, region: spot.id, unitType: prod.unitType });
          } else refund(g.factions[reg.owner]!.stock, prod.cost);
        }
      }
      reg.production = null;
    }
  }
}

/** Run one contiguous run of AI factions in order: each one takes its turn, we attribute any HUMAN region it
 *  took (diffing after EACH faction, since a tile can flip twice in a phase; keyed "attacker:victim"), and
 *  yield the id of every new offer it makes so the interactive caller can prompt before the next AI acts. */
function* runAiSegment(g: GameState, ids: FactionId[], lost: Map<string, number>, prevOwner: FactionId[], humans: Set<FactionId>): Generator<number, void, void> {
  for (const fid of ids) {
    if (g.factions[fid]!.eliminated) continue;
    const offersBefore = new Set(g.offers.map((o) => o.id));
    resolveVoyages(g, fid); // the walk reached this seat: its boats land now, human or AI alike
    runAiTurn(g, fid);
    for (let i = 0; i < g.regions.length; i++) {
      const now = g.regions[i]!.owner;
      const was = prevOwner[i]!;
      if (was !== now && humans.has(was)) {
        const k = `${now}:${was}`;
        lost.set(k, (lost.get(k) ?? 0) + 1);
      }
      prevOwner[i] = now;
    }
    for (const o of g.offers) if (!offersBefore.has(o.id)) yield o.id;
  }
}

/** Advance play from the CURRENT human's end-of-turn to the next human's start-of-turn (g.turnOf tracks who).
 *  The play order is fixed per game (see turnOrder): the AI seats between the two humans each take their turn,
 *  and when the walk crosses the end of the order the round ticks over (income, research, regen, production)
 *  ONCE. With a single human (solo) this is exactly the classic post-player AIs → upkeep → pre-player AIs
 *  phase. `opening=true` instead runs only the AIs seated BEFORE the first human of round 1 (the game's
 *  opening), with no round tick. Yields each new AI→human offer id so the interactive UI can surface it;
 *  drain it (endTurn) for headless play. Deterministic, so replays and headless sims stay reproducible. */
export function* runTurnGen(g: GameState, opening = false): Generator<number, void, void> {
  if (g.phase !== 'playing') return;
  const order = turnOrderOf(g);
  const humans = new Set(g.factions.filter((f) => f.isPlayer).map((f) => f.id));
  const livingHuman = (id: FactionId): boolean => humans.has(id) && !g.factions[id]!.eliminated;

  // record the board the next human will watch the AI-phase replay against, and start a fresh replay.
  // g.log is cleared HERE (not just rebuilt at the end) so notes raised during the walk itself, e.g. a
  // fleet reporting that its beach was taken, survive into the feed instead of being clobbered.
  g.replayBefore = g.regions.map((r) => ({ owner: r.owner, unit: r.unit ? { ...r.unit } : null, buildings: [...r.buildings] }));
  g.replay = [];
  g.log = [];
  const key = (list: [FactionId, FactionId][]): Set<string> => new Set(list.map(([a, b]) => `${a}-${b}`));
  const beforeAlliances = key(g.alliances);
  const beforeWars = key(g.wars);
  const beforeAlive = g.factions.map((f) => f.eliminated);
  const lost = new Map<string, number>();
  const prevOwner = g.regions.map((r) => r.owner);

  if (opening) {
    // round 1 opening: run the AI seats before the first human in play order, no round tick
    const first = order.find((id) => livingHuman(id));
    const pre = first === undefined ? [...order] : order.slice(0, order.indexOf(first));
    yield* runAiSegment(g, pre, lost, prevOwner, humans);
    if (first !== undefined) {
      resolveVoyages(g, first); // a human turn starts: their boats land first (mirrors runAiTurn's seam)
      g.turnOf = first;
    }
  } else {
    const actor = g.turnOf ?? g.playerFaction; // the human whose turn is ENDING now
    // an offer lives exactly one owner-turn: whatever THIS human left unanswered is treated as rejected
    // (escrow refunded); offers aimed at OTHER humans stay pending until their own turns end.
    for (const o of g.offers) if (o.to === actor) refundOfferEscrow(g, o);
    g.offers = g.offers.filter((o) => o.to !== actor);
    // walk the play order from just after the actor: AI seats take their turns, the round ticks over at
    // the wrap, and the walk stops at the next living human (solo: back to the player, one full lap).
    let i = order.indexOf(actor);
    for (let steps = 0; steps < order.length + 1; steps++) {
      i += 1;
      if (i >= order.length) {
        i = 0;
        roundUpkeep(g); // round boundary: income, research, regen, production completes
        g.round += 1;
        // past the final round: STOP before any post-wrap AI plays. Previously the walk let AI seats
        // ordered before the human take a round-(limit) turn the human never got, and those free
        // captures could flip a razor-thin round-cap finish in a phase the player cannot answer.
        if (g.round > g.roundLimit) break;
      }
      const fid = order[i]!;
      if (livingHuman(fid)) {
        resolveVoyages(g, fid); // a human turn starts: their boats land first (mirrors runAiTurn's seam)
        g.turnOf = fid;
        break;
      }
      if (!g.factions[fid]!.eliminated && !humans.has(fid)) yield* runAiSegment(g, [fid], lost, prevOwner, humans);
    }
  }

  // --- diff → turn log ---
  const log: LogEvent[] = [];
  for (const [k, n] of lost) {
    const [att, vic] = k.split(':').map(Number) as [FactionId, FactionId];
    // solo keeps the classic second-person line; multiplayer names the victim (state stays identical on every client)
    const whose = g.mode === 'mp' ? `${g.factions[vic]!.name}'s` : 'your';
    log.push({ kind: 'attack', text: `⚔ ${g.factions[att]!.name} seized ${n} of ${whose} region${n > 1 ? 's' : ''}!` });
  }
  for (const [a, b] of g.wars) if (!beforeWars.has(`${a}-${b}`)) log.push({ kind: 'attack', text: `⚔ ${g.factions[a]!.name} declared war on ${g.factions[b]!.name}.` });
  for (const [a, b] of g.alliances) if (!beforeAlliances.has(`${a}-${b}`)) log.push({ kind: 'diplo', text: `⭐ ${g.factions[a]!.name} & ${g.factions[b]!.name} formed an alliance.` });
  for (let i = 0; i < g.factions.length; i++) if (!beforeAlive[i] && g.factions[i]!.eliminated) log.push({ kind: 'defeat', text: `💀 ${g.factions[i]!.name} has fallen.` });
  g.log = [...g.log, ...log]; // walk-time notes (fleet reports) first, then the diff summary

  // inbox shown after the replay: alliance-break cards pushed during the walk are KEPT (wiping them here
  // made breakAlliance's betrayal notice dead on arrival - no client ever got to show it), then war
  // declarations on any human are added (each client shows its own); offers were already prompted
  // mid-sequence above, so answers could influence the AIs that acted after
  g.messages = g.messages.filter((m) => m.kind === 'allyBroken');
  for (const [a, b] of g.wars) {
    if (beforeWars.has(`${a}-${b}`)) continue;
    if (humans.has(a)) g.messages.push({ kind: 'war', from: b, to: a });
    if (humans.has(b)) g.messages.push({ kind: 'war', from: a, to: b });
  }

  checkGameEnd(g);
}

/** One full all-AI round for headless simulations (balance studies, AI-takeover turns): every living seat
 *  plays in order as an AI, then the round ticks over. Resets the replay buffer so long sims don't grow it. */
export function runFullAiRound(g: GameState): void {
  if (g.phase !== 'playing') return;
  g.replay = [];
  g.replayBefore = [];
  for (const fid of turnOrderOf(g)) {
    if (g.factions[fid]!.eliminated) continue;
    resolveVoyages(g, fid); // same walk seam as runTurnGen: boats land as the walk reaches their owner
    runAiTurn(g, fid);
  }
  roundUpkeep(g);
  g.round += 1;
  checkGameEnd(g);
}

/** Non-interactive full turn (headless play / the AI-vs-AI sim): run the generator to completion. Any
 *  AI→player offers just stay pending in g.offers; the interactive UI drives runTurnGen to prompt them. */
export function endTurn(g: GameState): void {
  const gen = runTurnGen(g);
  while (!gen.next().done) {
    /* no player to answer offers in headless play */
  }
}

/** Hand a human seat to the AI (multiplayer AFK/disconnect takeover). Applied at the same command-stream
 *  position on every client, so the sim stays in lockstep; from the next round the turn driver simply
 *  treats the seat as an AI. Idempotent. */
export function aiTakeover(g: GameState, seat: FactionId): void {
  const f = g.factions[seat];
  if (!f) return;
  f.isPlayer = false;
  // that was the LAST living human: finish now, crowning the current leader, so any still-connected
  // client shows a result instead of stalling forever. (checkGameEnd's mp finish needs some(isPlayer),
  // which this flip just falsified, so it can never fire on its own after this point.)
  if (g.mode === 'mp' && g.phase === 'playing' && !g.factions.some((x) => x.isPlayer && !x.eliminated)) {
    finishMatch(g, leaderOf(g, regionCounts(g)));
  }
}

/** The mirror of aiTakeover: a taken-over seat's owner returned, so the seat is human again from now on
 *  (aiDifficulty is left as-is). Ordered through the same seq stream as commands, so every client flips
 *  it at the same point. Idempotent. */
export function reclaimSeat(g: GameState, seat: FactionId): void {
  const f = g.factions[seat];
  if (f) f.isPlayer = true;
}

/** How many regions each faction controls (for the ranking / victory check). */
export function regionCounts(g: GameState): number[] {
  const counts = new Array<number>(g.factions.length).fill(0);
  for (const reg of g.regions) if (reg.owner >= 0) counts[reg.owner]! += 1;
  return counts;
}

/** The tiles a realm can actually OWN. Impassable scenery (cliffs, castle masonry) is born neutral and
 *  can never be captured, so it must stay out of every share-of-the-map denominator: with it counted,
 *  100% domination was unreachable on any board with a single mountain. */
export function conquerableTotal(g: GameState): number {
  let n = 0;
  for (const reg of g.regions) if (!impassable(reg.terrain)) n++;
  return n;
}
